import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "components/Layout";
import OptionPriceDisplay from "components/OptionPriceDisplay";
import Calendar, { toISODate } from "components/Calendar";
import api from "utils/api";
import { setLogout, setUser } from "state";
import {
  DR_HOFFMANN_SERVICES,
  formatPrice,
  individualServicePriceForPaymentMethod,
  priceForPaymentMethod,
  wakeupUnitPriceForPaymentMethod,
} from "constants/massages";
import {
  isHoffmannVoucherActive,
  isStudioLikeBooking,
} from "utils/hoffmannVoucher";
import { buildBuchenAuthHref } from "utils/registration";
import { isReferralBalanceEligibleOption } from "utils/referral";
import "styles/booking.css";

const BERLIN_TZ = "Europe/Berlin";
const VAT_RATE = 0.19;
export const PRICE_PREVIEW_BRAND = "dr-hoffmann";
export const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: BERLIN_TZ,
  });
export const formatLong = (date) =>
  date.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: BERLIN_TZ,
  });
const OPTIONS = [
  {
    id: "wakeup",
    deliveryGroup: "wakeup",
    serviceKey: "dr-hoffmann-wakeup-studio",
    availabilityServiceKey: "dr-hoffmann-studio",
    offerType: "wakeup_studio",
    tag: "Option 1",
    title: "Wake-Up",
    subtitle: "Energie in wenigen Minuten",
    description:
      "Unsere Wake-up-Massage ist die schnelle 4-in-1-Aktivierung für deinen Körper. Sie fördert die Durchblutung, löst Verspannungen, unterstützt die Regeneration und verbessert die Beweglichkeit. Ideal, wenn du schnell neue Energie brauchst.",
    price: "5 €",
    priceNote: "p. P.",
    displayTotalLabel: "5 € p. P.",
    cta: "Termin auswählen",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "studio-weekday",
    studioLocation: "Cremon 11, 20457 Hamburg",
    disableOnlineDiscount: false,
    personCountDefault: 1,
    personCountMin: 1,
    personCountMax: 10,
    durationMinutes: 5,
    minimumLeadMinutes: 30,
  },
  {
    id: "classic",
    deliveryGroup: "classic",
    serviceKey: "dr-hoffmann-classic-studio",
    availabilityServiceKey: "dr-hoffmann-studio",
    offerType: "classic_studio",
    tag: "Option 2",
    title: "Classic",
    subtitle: "Ganzkörper-Erholung für die Woche",
    description:
      "Unsere Classic-Massage ist die gezielte Ganzkörpermassage für nachhaltige Erholung und neue Energie. Mit der perkussiven Massage werden Verspannungen und Verklebungen im gesamten Körper intensiv bearbeitet, die Durchblutung gefördert und die Regeneration unterstützt. Nach einer kurzen Erholungsphase fühlt sich der Körper wieder freier, beweglicher und voller Energie an – ideal für einen ausgeglichenen Energiefluss durch die ganze Woche.",
    price: "29 €",
    priceNote: "/ 10 Min.",
    displayTotalLabel: "29 € / 10 Min.",
    spotlight: "Meist gebucht",
    cta: "Termin auswählen",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "studio-weekday",
    studioLocation: "Cremon 11, 20457 Hamburg",
    disableOnlineDiscount: false,
    durationMinutes: 10,
    priceCents: 2900,
    minimumLeadMinutes: 30,
  },
  {
    id: "premium",
    deliveryGroup: "premium",
    serviceKey: "dr-hoffmann-premium-studio",
    availabilityServiceKey: "dr-hoffmann-studio",
    offerType: "premium_studio",
    tag: "Option 3",
    title: "Intense",
    subtitle: "Wellness für Körper und Geist",
    description:
      "1 Stunde. 1 Trainer. 100 % individuell. Intense ist deine persönliche Dr.-Hoffmann-Begleitung – im Hotel, im Studio oder draußen. Ein erfahrener Trainer geht individuell auf dich ein: mit Bewegung, Stretching, Entspannung, Perkussionsmassage oder Beratung zu Training, Essen, Trinken und Regeneration. Genau das, was dein Körper gerade braucht.",
    price: "ab 70 €",
    priceNote: "/ 30 Min.",
    displayTotalLabel: "ab 70 € / 30 Min.",
    cta: "Termin auswählen",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "studio-weekday",
    studioLocation: "Cremon 11, 20457 Hamburg",
    disableOnlineDiscount: false,
    durationMinutes: 60,
    durationDefault: 60,
    durationOptions: [30, 60],
    priceCents: 7000,
    minimumLeadMinutes: 30,
  },
];

export const WAKEUP_STUDIO_OPTION = OPTIONS.find((option) => option.id === "wakeup");
export const CLASSIC_STUDIO_OPTION = OPTIONS.find((option) => option.id === "classic");
export const PREMIUM_STUDIO_OPTION = OPTIONS.find((option) => option.id === "premium");
export const WAKEUP_ONSITE_OPTION = {
  ...WAKEUP_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-wakeup-onsite",
  legacyServiceKey: "dr-hoffmann-wakeup-onsite-voucher",
  availabilityServiceKey: "dr-hoffmann-mobile",
  offerType: "wakeup_onsite",
  description:
    "Unsere Wake-up-Massage ist die schnelle 4-in-1-Aktivierung für deinen Körper. Sie fördert die Durchblutung, löst Verspannungen, unterstützt die Regeneration und verbessert die Beweglichkeit. Ideal, wenn du schnell neue Energie brauchst.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € - mit Gutschein 0 €",
  hint: "Gutschein nur einmalig einlösbar und nicht kombinierbar.",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};
export const CLASSIC_ONSITE_OPTION = {
  ...CLASSIC_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-classic-onsite",
  legacyServiceKey: "dr-hoffmann-mobile",
  availabilityServiceKey: "dr-hoffmann-mobile",
  offerType: "classic_onsite",
  description:
    "Unsere Classic-Massage ist die gezielte Ganzkörpermassage für nachhaltige Erholung und neue Energie. Mit der perkussiven Massage werden Verspannungen und Verklebungen im gesamten Körper intensiv bearbeitet, die Durchblutung gefördert und die Regeneration unterstützt. Nach einer kurzen Erholungsphase fühlt sich der Körper wieder freier, beweglicher und voller Energie an – ideal für einen ausgeglichenen Energiefluss durch die ganze Woche.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € - mit Gutschein 0 €",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};
export const PREMIUM_ONSITE_OPTION = {
  ...PREMIUM_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-premium-onsite",
  availabilityServiceKey: "dr-hoffmann-mobile",
  offerType: "premium_onsite",
  description:
    "1 Stunde. 1 Trainer. 100 % individuell. Intense ist deine persönliche Dr.-Hoffmann-Begleitung – im Hotel, im Studio oder draußen. Ein erfahrener Trainer geht individuell auf dich ein: mit Bewegung, Stretching, Entspannung, Perkussionsmassage oder Beratung zu Training, Essen, Trinken und Regeneration. Genau das, was dein Körper gerade braucht.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € - mit Gutschein 0 €",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};

const HOFFMANN_BUSINESS_EVENT_BOOKING_CUTOFF = new Date("2026-06-14T16:00:00+02:00");
const HOFFMANN_BUSINESS_EVENT_START_ISO = "2026-06-14T16:00:00+02:00";
const HOFFMANN_BUSINESS_EVENT_SCHEDULE_LABEL =
  "14.06.2026 - Ab 16 Uhr - 19 Uhr Anpfiff";
const HOFFMANN_BUSINESS_EVENT_LOCATION = "Cremon 11, 20457 Hamburg";
const HOFFMANN_BUSINESS_EVENT_MAX_SEATS_PER_USER = 10;

export const BUSINESS_OPTIONS = [
  {
    id: "wakeup-plus-business",
    serviceKey: "dr-hoffmann-wakeup-plus-business",
    availabilityServiceKey: "dr-hoffmann-mobile",
    offerType: "wakeup_plus_business",
    tag: "Option 2",
    title: "Wake-Up+ für Unternehmen",
    description:
      "Für Unternehmen, die ihre Mitarbeiter langanhaltend fit und produktiv halten möchten. Wake-Up Massage für Teams ab 1 Person.",
    price: "ab 79 €",
    priceNote: "netto zzgl. MwSt.",
    displayTotalLabel: "ab 79 € netto zzgl. MwSt.",
    cta: "Termin auswählen",
    requiresAuth: true,
    requiresAddress: true,
    bookingMode: "mobile",
    disableOnlineDiscount: true,
    skipPostalCheck: true,
    personCountDefault: 10,
    personCountMin: 1,
    durationMinutes: 10,
  },
];

const SPECIAL_REQUEST_OPTION = {
  id: "special-request",
  tag: "Option 6",
  title: "Sonderwünsche?",
  description:
    "Für größere Gruppen, Events, Adressen außerhalb Hamburgs oder individuelle Wünsche.",
  price: "Auf Anfrage",
  cta: "Sonderanfrage stellen",
  requiresAuth: false,
  specialRequestPath: "/kontakt?reason=sonderwunsch",
};

export const LEGACY_OPTIONS = [
  {
    id: "starterpaket",
    serviceKey: "dr-hoffmann-consultation",
    tag: "Option 4",
    title: "Starterpaket buchen",
    description:
      "Inklusive attraktivem Neukundenpaket: 6 kostenfreie Massagen vor Ort, ein Social-Media-Blumenstrauß sowie ein professioneller Werbefilm für das Unternehmen nach Wahl - ideal zur Neukundengewinnung.",
    price: "499 €",
    priceCents: Math.round(49900 * (1 + VAT_RATE)),
    priceNetCents: 49900,
    priceNote: "netto zzgl. MwSt.",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "consultation",
    paidPackage: true,
    stripeOnly: true,
    hoffmannBookingOption: "Starterpaket buchen",
    durationMinutes: 30,
  },
  {
    id: "lizenzpartner",
    serviceKey: "dr-hoffmann-consultation",
    tag: "Option 5",
    title: "Lizenzpartner werden",
    highlight: "Kostenloser Erstbesuch inkl.",
    description:
      "bis zu 6 Test-Massagen und Location Check.",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "consultation",
    hoffmannBookingOption: "Lizenzpartner werden",
    durationMinutes: 30,
  },
];

const ALL_OPTIONS = [...OPTIONS, ...BUSINESS_OPTIONS, ...LEGACY_OPTIONS];
const AUDIENCE_INDIVIDUAL = "individual";
const AUDIENCE_BUSINESS = "business";
const normalizeAudienceId = (id) =>
  id === AUDIENCE_BUSINESS ? AUDIENCE_BUSINESS : AUDIENCE_INDIVIDUAL;
const BOOKING_AUDIENCES = [
  {
    id: AUDIENCE_INDIVIDUAL,
    label: "Privat",
    title: "Für Privatpersonen und flexible Termine",
    description: "Wake-Up, Classic, Intense Massagen und individuelle Anfragen.",
  },
  {
    id: AUDIENCE_BUSINESS,
    label: "Geschäftlich",
    title: "Für Unternehmen und Partner",
    description: "Wake-Up+ für Teams, Starterpaket und Lizenzpartnerschaft.",
  },
];
const BUSINESS_ONLY_OPTION_IDS = new Set(
  [...BUSINESS_OPTIONS, ...LEGACY_OPTIONS]
    .map((option) => option.id)
);
const audienceForOption = (option) =>
  BUSINESS_ONLY_OPTION_IDS.has(option?.id) ? AUDIENCE_BUSINESS : AUDIENCE_INDIVIDUAL;
const BOTTOM_PRICE_OPTION_IDS = new Set([
  "wakeup-plus-business",
]);
const DELIVERY_STUDIO_OPTIONS = {
  wakeup: WAKEUP_STUDIO_OPTION,
  classic: CLASSIC_STUDIO_OPTION,
  premium: PREMIUM_STUDIO_OPTION,
};
const DELIVERY_ONSITE_OPTIONS = {
  wakeup: WAKEUP_ONSITE_OPTION,
  classic: CLASSIC_ONSITE_OPTION,
  premium: PREMIUM_ONSITE_OPTION,
};
const deliveryGroupForOption = (option) => option?.deliveryGroup || option?.id;
export const isDeliveryChoiceOption = (option) =>
  Boolean(DELIVERY_STUDIO_OPTIONS[deliveryGroupForOption(option)]);
export const optionForDelivery = (optionOrGroup, comesToYou) => {
  const group =
    typeof optionOrGroup === "string"
      ? optionOrGroup
      : deliveryGroupForOption(optionOrGroup);
  return comesToYou
    ? DELIVERY_ONSITE_OPTIONS[group]
    : DELIVERY_STUDIO_OPTIONS[group];
};
const wakeupComesToYouFromId = (id) =>
  id === "wakeup-onsite" ||
  id === "wakeup-onsite-voucher" ||
  id === "classic-onsite" ||
  id === "premium-onsite";

export const defaultPaymentForOption = (option) =>
  isBusinessEventOption(option)
    ? "on-site"
    : requiresStripeOnlyPayment(option)
    ? "stripe"
    : option?.bookingMode === "consultation" && !option?.paidPackage
    ? "later"
    : "stripe";

export const defaultPersonCountForOption = (option) =>
  Number(option?.personCountDefault || 1);

export const defaultDurationForOption = (option) =>
  Number(option?.durationDefault ?? option?.durationMinutes ?? 60);

export const initialDurationMinutesForOption = (option) =>
  durationChoicesForOption(option).length > 0
    ? ""
    : defaultDurationForOption(option);

export const durationChoicesForOption = (option) => {
  if (!option?.durationOptions?.length) return [];
  return option.durationOptions.map((minutes) => Number(minutes));
};

export const isPremiumBookingOption = (option) =>
  option?.offerType === "premium_studio" || option?.offerType === "premium_onsite";

export const isWakeupPlusOption = (option) =>
  option?.offerType === "wakeup_plus_business";

export const isWakeupMobileOption = (option) =>
  option?.offerType === "wakeup_onsite" || option?.offerType === "wakeup_plus_business";

export const isWakeupBookingOption = (option) =>
  String(option?.offerType || "").startsWith("wakeup_");

export const isDeliveryOnsiteOption = (option) =>
  ["wakeup_onsite", "classic_onsite", "premium_onsite"].includes(
    option?.offerType
  );

export const hasAdjustableTravelFee = (option) =>
  isDeliveryOnsiteOption(option) && option?.offerType !== "wakeup_plus_business";

export const isFixedGrossIndividualOption = (option) =>
  [
    "wakeup_studio",
    "wakeup_onsite",
    "classic_studio",
    "classic_onsite",
    "premium_studio",
    "premium_onsite",
  ].includes(option?.offerType);

export const usesIndividualSchedule = (option) =>
  isFixedGrossIndividualOption(option) && !isDeliveryOnsiteOption(option);

export const usesDailyHoffmannSchedule = (option) =>
  usesIndividualSchedule(option) || isDeliveryOnsiteOption(option);

export const availabilityServiceKeyForOption = (option) => {
  if (option?.offerType === "wakeup_studio") return "dr-hoffmann-wakeup-studio";
  if (option?.offerType === "wakeup_onsite") return "dr-hoffmann-wakeup-onsite";
  if (option?.offerType === "classic_studio") return "dr-hoffmann-classic-studio";
  if (option?.offerType === "classic_onsite") return "dr-hoffmann-classic-onsite";
  if (option?.offerType === "premium_studio") return "dr-hoffmann-premium-studio";
  if (option?.offerType === "premium_onsite") return "dr-hoffmann-premium-onsite";
  if (option?.offerType === "wakeup_plus_business") {
    return "dr-hoffmann-wakeup-plus-business";
  }
  if (usesIndividualSchedule(option)) return "dr-hoffmann-studio";
  return option?.availabilityServiceKey || option?.serviceKey;
};

export const WAKEUP_MINUTES_PER_PERSON = 5;

export const wakeupServiceDurationMinutes = (option, count) =>
  isWakeupPlusOption(option)
    ? Number(option?.durationMinutes || 10)
    : isWakeupMobileOption(option)
    ? Math.max(1, Number(count || option?.personCountDefault || 1) * WAKEUP_MINUTES_PER_PERSON)
    : WAKEUP_MINUTES_PER_PERSON;

const fullAddressFromServiceAddress = (addr) => {
  if (!addr || typeof addr !== "object") return "";
  const explicit = String(addr?.fullAddress || addr?.address || "").trim();
  if (explicit) return explicit;
  const street = String(addr?.street || "").trim();
  const postalCode = String(addr?.postalCode || "").trim();
  const city = String(addr?.city || "").trim();
  return [street, postalCode, city].filter(Boolean).join(" ").trim();
};

const extractPlzFromText = (value) => {
  const match = String(value || "").match(/\b(\d{5})\b/);
  return match ? match[1] : "";
};

const buildServiceAddress = ({ companyName, fullAddress, notes, geocodedAddress }) => {
  const postalCode =
    extractPlzFromText(geocodedAddress) || extractPlzFromText(fullAddress);
  return {
    companyName,
    fullAddress,
    address: fullAddress,
    street: fullAddress,
    postalCode,
    city: "",
    notes,
  };
};

export const buildServiceAddressForOption = (option, addr) => {
  if (option?.usesLegacyAddressFields) return addr;
  return buildServiceAddress(addr);
};

export const buildLocalPricePreview = (
  option,
  count,
  hasVoucher = false,
  applyTravelFee = true,
  paymentMethod = "on-site",
  user = null,
  durationMinutes
) => {
  if (!option?.offerType) return null;
  const personCount = Math.max(1, Number(count || option.personCountDefault || 1));
  const selectedDurationMinutes = Number(
    durationMinutes ?? option?.durationMinutes ?? 60
  );
  let serviceNetCents = 0;
  let travelNetCents;
  let travelOriginalNetCents;
  if (option.offerType === "wakeup_studio") {
    serviceNetCents =
      personCount * wakeupUnitPriceForPaymentMethod(paymentMethod, user);
  } else if (option.offerType === "wakeup_onsite") {
    serviceNetCents =
      personCount * wakeupUnitPriceForPaymentMethod(paymentMethod, user);
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "classic_studio") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(option.serviceKey, paymentMethod) ?? 2900;
  } else if (option.offerType === "classic_onsite") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(option.serviceKey, paymentMethod) ?? 2900;
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "premium_studio") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(
        option.serviceKey,
        paymentMethod,
        selectedDurationMinutes
      ) ?? (selectedDurationMinutes === 30 ? 8000 : 12500);
  } else if (option.offerType === "premium_onsite") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(
        option.serviceKey,
        paymentMethod,
        selectedDurationMinutes
      ) ?? (selectedDurationMinutes === 30 ? 8000 : 12500);
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "wakeup_plus_business") {
    serviceNetCents = 7900 + Math.max(0, personCount - 10) * 500;
    travelNetCents = 0;
  } else if (option.offerType === "business_event") {
    const unitGrossCents = Number(option.unitPriceCents || option.priceCents || 0);
    const grossCents = unitGrossCents * personCount;
    const netCents = Math.round(grossCents / (1 + VAT_RATE));
    return {
      netCents,
      vatCents: grossCents - netCents,
      grossCents,
      source: "local",
    };
  } else {
    return null;
  }
  const netCents = serviceNetCents + (travelNetCents || 0);
  const vatCents = isFixedGrossIndividualOption(option)
    ? 0
    : Math.round(netCents * VAT_RATE);
  return {
    serviceNetCents,
    travelNetCents,
    travelOriginalNetCents,
    netCents,
    vatCents,
    grossCents: netCents + vatCents,
    source: "local",
  };
};

export const normalizePricePreview = (data) => {
  const source = data?.pricePreview || data?.preview || data;
  const serviceNetCents = Number(
    source?.serviceNetCents ?? source?.serviceAmountNetCents
  );
  const travelNetCents = Number(
    source?.travelNetCents ?? source?.travelAmountNetCents ?? source?.arrivalNetCents
  );
  const travelOriginalNetCents = Number(
    source?.travelOriginalNetCents ?? source?.arrivalOriginalNetCents
  );
  const netCents = Number(source?.netCents ?? source?.netAmountCents);
  const vatCents = Number(source?.vatCents ?? source?.taxCents);
  const grossCents = Number(source?.grossCents ?? source?.totalCents ?? source?.priceCents);
  if (![netCents, vatCents, grossCents].every(Number.isFinite)) return null;
  return {
    serviceNetCents: Number.isFinite(serviceNetCents) ? serviceNetCents : undefined,
    travelNetCents: Number.isFinite(travelNetCents) ? travelNetCents : undefined,
    travelOriginalNetCents: Number.isFinite(travelOriginalNetCents)
      ? travelOriginalNetCents
      : undefined,
    netCents,
    vatCents,
    grossCents,
    source: "backend",
  };
};

const normalizeOptionId = (id) => {
  if (
    id === "wakeup-studio" ||
    id === "wakeup-onsite" ||
    id === "wakeup-onsite-voucher"
  ) {
    return "wakeup";
  }
  if (id === "classic-studio" || id === "classic-onsite") {
    return "classic";
  }
  if (id === "premium-studio" || id === "premium-onsite") {
    return "premium";
  }
  if (id === "voucher-studio" || id === "voucher-chair" || id === "sessl") {
    return "studio";
  }
  if (id === "business-event") {
    return "wm-aktion";
  }
  return id;
};

export const isThursdayOnlyStudio = (option) => option?.bookingMode === "studio";
export const isLicensePartnerOption = (option) => option?.id === "lizenzpartner";
export const isStarterPackageOption = (option) => option?.id === "starterpaket";
export const isBusinessEventOption = (option) =>
  option?.offerType === "business_event" ||
  option?.id === "wm-aktion" ||
  option?.id === "business-event";
const requiresStripeOnlyPayment = (option) =>
  Boolean(option?.stripeOnly) ||
  isWakeupPlusOption(option) ||
  (Boolean(option?.paidPackage) && !isBusinessEventOption(option));
const disablesOnsitePaymentChoice = (option) =>
  isFixedGrossIndividualOption(option);
const paymentMethodForOption = (option, paymentMethod) =>
  disablesOnsitePaymentChoice(option) && paymentMethod === "on-site"
    ? "stripe"
    : paymentMethod;
const isAfterBusinessEventCutoff = (date = new Date()) =>
  date.getTime() >= HOFFMANN_BUSINESS_EVENT_BOOKING_CUTOFF.getTime();
export const skipsTimeslotSelection = (option) =>
  isStarterPackageOption(option) || isBusinessEventOption(option);

export const bookingHoursHintForOption = (option, date) => {
  if (!option || isStarterPackageOption(option)) return null;
  if (isThursdayIndividualStudioBooking(option, date)) {
    return "Termine sind zwischen 10:00 und 22:00 Uhr buchbar.";
  }
  if (isThursdayOnlyStudio(option) || isLicensePartnerOption(option)) {
    return "Termine sind zwischen 12:00 und 19:30 Uhr buchbar.";
  }
  if (isBusinessEventOption(option)) {
    return null;
  }
  if (
    usesIndividualSchedule(option) ||
    isDeliveryOnsiteOption(option) ||
    isWakeupPlusOption(option)
  ) {
    if (option.offerType === "wakeup_studio") {
      return "Termine sind zwischen 10:00 und 22:00 Uhr buchbar. Im Studio w\u00e4hlbar in 5-Minuten-Abst\u00e4nden.";
    }
    return "Termine sind zwischen 10:00 und 22:00 Uhr buchbar.";
  }
  return null;
};

const berlinWeekdayIndex = (date) => {
  const shortWeekday = new Intl.DateTimeFormat("en-US", {
    timeZone: BERLIN_TZ,
    weekday: "short",
  }).format(new Date(date));
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(shortWeekday);
};

export const isThursdayIndividualStudioBooking = (option, date) =>
  Boolean(option && date && usesIndividualSchedule(option) && berlinWeekdayIndex(date) === 4);

export const filterSlotsForOption = (_option, _date, slotList) => {
  if (!Array.isArray(slotList)) return [];
  return slotList;
};

export const studioScheduleForOption = (option, date) =>
  usesIndividualSchedule(option)
    ? "weekday"
    : isThursdayIndividualStudioBooking(option, date)
    ? "thursday"
    : "weekday";

export const slotStepMinutesForOption = (option) =>
  option?.offerType === "classic_studio" || isPremiumBookingOption(option)
    ? 15
    : undefined;

const HOFFMANN_OPTION1_CUTOFF = new Date(2026, 5, 30, 23, 59, 59, 999);
export const isAfterHoffmannOption1Cutoff = (date) =>
  Boolean(date) && new Date(date).getTime() > HOFFMANN_OPTION1_CUTOFF.getTime();

export const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addBusinessDays = (date, days) => {
  const result = startOfDay(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const weekday = result.getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }
  return result;
};

export const licensePartnerMinDate = () => addBusinessDays(new Date(), 2);

export const arrangedAppointmentStartISO = () => {
  const placeholder = addBusinessDays(new Date(), 10);
  placeholder.setHours(12, 0, 0, 0);
  return placeholder.toISOString();
};

const isFreeConsultation = (option) =>
  option?.bookingMode === "consultation" && !option?.paidPackage;

const CREMON_STUDIO_LOCATION = "Cremon 11, 20457 Hamburg";
const DEFAULT_STUDIO_LOCATION =
  DR_HOFFMANN_SERVICES.find((s) => s.key === "dr-hoffmann-studio")?.locationDefault ||
  CREMON_STUDIO_LOCATION;

const studioLocationForDate = () => DEFAULT_STUDIO_LOCATION;

export const studioLocationForOption = (option, date) =>
  usesIndividualSchedule(option)
    ? CREMON_STUDIO_LOCATION
    : option?.studioLocation || studioLocationForDate(date);

const BOOKING_DRAFT_STORAGE_KEY = "drHoffmannBookingDraft";
const initialBookingConsents = {
  legalTerms: false,
};

const readBookingDraft = () => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(BOOKING_DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveBookingDraft = (draft) => {
  if (typeof sessionStorage === "undefined" || !draft) return;
  try {
    sessionStorage.setItem(BOOKING_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* quota / private mode */
  }
};

const clearBookingDraft = () => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const PersonCountCounter = ({ id, value, min = 1, max, onChange }) => {
  const holdTimerRef = useRef(null);
  const holdIntervalRef = useRef(null);
  const nextValue = useCallback(
    (delta) => {
      const numeric = Number(value) || min;
      const upper = Number.isFinite(Number(max)) ? Number(max) : Infinity;
      const bounded = Math.min(upper, Math.max(min, numeric + delta));
      onChange(bounded);
    },
    [max, min, onChange, value]
  );
  const stopHold = useCallback(() => {
    window.clearTimeout(holdTimerRef.current);
    window.clearInterval(holdIntervalRef.current);
    holdTimerRef.current = null;
    holdIntervalRef.current = null;
  }, []);
  const startHold = useCallback(
    (delta) => {
      nextValue(delta);
      stopHold();
      holdTimerRef.current = window.setTimeout(() => {
        holdIntervalRef.current = window.setInterval(() => nextValue(delta), 90);
      }, 350);
    },
    [nextValue, stopHold]
  );

  useEffect(() => stopHold, [stopHold]);

  return (
    <div className="person-counter">
      <button
        type="button"
        className="person-counter-btn"
        onPointerDown={() => startHold(-1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") startHold(-1);
        }}
        onKeyUp={stopHold}
        disabled={Number(value) <= min}
        aria-label="Mitarbeitende verringern"
      >
        -
      </button>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="person-counter-btn"
        onPointerDown={() => startHold(1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") startHold(1);
        }}
        onKeyUp={stopHold}
        disabled={Number.isFinite(Number(max)) && Number(value) >= Number(max)}
        aria-label="Mitarbeitende erhöhen"
      >
        +
      </button>
    </div>
  );
};

const draftFromState = ({
  selectedOption,
  selectedDate,
  selectedSlot,
  consultationNotes,
  address,
  serviceAreaCheck,
  paymentMethod,
  personCount,
  durationMinutes,
  hasVoucher,
  voucherTermsAccepted,
  bookingPhone,
  wakeupComesToYou,
}) => ({
  optionId: selectedOption?.id || null,
  selectedDate: selectedDate ? toISODate(selectedDate) : null,
  selectedSlot,
  consultationNotes,
  address,
  serviceAreaCheck,
  paymentMethod,
  personCount,
  durationMinutes,
  hasVoucher,
  voucherTermsAccepted,
  bookingPhone,
  wakeupComesToYou,
});

const BookingPage = () => {
  const user = useSelector((s) => s.user);
  const token = useSelector((s) => s.token);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const optionDetailRef = useRef(null);
  const restoredDraftRef = useRef(false);

  const params = new URLSearchParams(location.search);
  const initialRawOptionId = params.get("option") || null;
  const initialAudience = normalizeAudienceId(params.get("audience"));
  const initialOptionId = normalizeOptionId(initialRawOptionId);
  const initialWakeupComesToYou = wakeupComesToYouFromId(initialRawOptionId);
  const requestedOption =
    DELIVERY_STUDIO_OPTIONS[initialOptionId]
      ? optionForDelivery(initialOptionId, initialWakeupComesToYou)
      : ALL_OPTIONS.find((o) => o.id === initialOptionId && !o.externalUrl) ||
        null;
  const initialOption = requestedOption;

  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [wakeupComesToYou, setWakeupComesToYou] = useState(
    initialWakeupComesToYou
  );
  const [bookingAudience, setBookingAudience] = useState(() =>
    initialOption ? audienceForOption(initialOption) : initialAudience
  );
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [consultationNotes, setConsultationNotes] = useState("");
  // Address fields (mobile option).
  const [address, setAddress] = useState({
    companyName: "",
    fullAddress: "",
    street: "",
    postalCode: "",
    city: "",
    notes: "",
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [serviceAreaCheck, setServiceAreaCheck] = useState({
    status: "idle",
    message: "",
  });
  const lastCheckedAddressRef = useRef("");
  // Allowed PLZ prefixes (loaded on first mobile-option open).
  const [postalRules, setPostalRules] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(() =>
    defaultPaymentForOption(initialOption)
  );
  const [personCount, setPersonCount] = useState(() =>
    defaultPersonCountForOption(initialOption)
  );
  const [durationMinutes, setDurationMinutes] = useState(() =>
    initialDurationMinutesForOption(initialOption)
  );
  const [pricePreview, setPricePreview] = useState(() => {
    const initialDuration = initialDurationMinutesForOption(initialOption);
    if (durationChoicesForOption(initialOption).length > 0 && !initialDuration) {
      return null;
    }
    return buildLocalPricePreview(
      initialOption,
      defaultPersonCountForOption(initialOption),
      false,
      true,
      defaultPaymentForOption(initialOption),
      null,
      initialDuration || defaultDurationForOption(initialOption)
    );
  });
  const [loadingPricePreview, setLoadingPricePreview] = useState(false);
  const [hasVoucher, setHasVoucher] = useState(null);
  const [voucherTermsAccepted, setVoucherTermsAccepted] = useState(false);
  const [bookingPhone, setBookingPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorAction, setErrorAction] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [applyReferralBalance, setApplyReferralBalance] = useState(true);
  const [bookingConsents, setBookingConsents] = useState(initialBookingConsents);

  const userHasPhone = Boolean(String(user?.phone || "").trim());
  const needsBookingPhone = Boolean(user && selectedOption && !userHasPhone);
  const pricingUser = useMemo(
    () =>
      user && !String(user.phone || "").trim() && String(bookingPhone || "").trim()
        ? { ...user, phone: bookingPhone.trim() }
        : user,
    [bookingPhone, user]
  );
  const selectedMinDate = isLicensePartnerOption(selectedOption)
    ? licensePartnerMinDate()
    : new Date();
  const selectedMaxDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
  const appointmentIsArranged = skipsTimeslotSelection(selectedOption);
  const businessEventBookingClosed =
    isBusinessEventOption(selectedOption) && isAfterBusinessEventCutoff();
  const onlinePaymentOnly = requiresStripeOnlyPayment(selectedOption);
  const onsitePaymentDisabled = disablesOnsitePaymentChoice(selectedOption);
  const effectivePaymentMethod = onlinePaymentOnly
    ? "stripe"
    : paymentMethodForOption(selectedOption, paymentMethod);
  const selectedStudioLocation = studioLocationForOption(
    selectedOption,
    selectedDate
  );
  const personCountMin = Number(selectedOption?.personCountMin || 1);
  const personCountMax = Number.isFinite(Number(selectedOption?.personCountMax))
    ? Number(selectedOption.personCountMax)
    : null;
  const selectedPersonCount = Math.max(
    personCountMin,
    Math.min(
      personCountMax ?? Infinity,
      Math.round(Number(personCount || defaultPersonCountForOption(selectedOption)))
    )
  );
  const selectedDurationMinutes =
    Number(durationMinutes) > 0 ? Number(durationMinutes) : null;
  const durationChoices = useMemo(
    () => durationChoicesForOption(selectedOption),
    [selectedOption]
  );
  const showDurationChoice = durationChoices.length > 0;
  const requiresOnsiteAddress = Boolean(selectedOption?.requiresAddress);
  const selectedFullAddress = fullAddressFromServiceAddress(address);
  const requiresServiceAreaCheck =
    isDeliveryOnsiteOption(selectedOption) ||
    selectedOption?.offerType === "wakeup_plus_business";
  const addressIsEligible = serviceAreaCheck.status === "eligible";
  const voucherChoiceRequired = Boolean(selectedOption?.requiresVoucherQuestion);
  const voucherChoiceMissing = voucherChoiceRequired && hasVoucher === null;
  const durationChoiceMissing = showDurationChoice && !selectedDurationMinutes;

  const isPaidBooking =
    Boolean(selectedOption?.paidPackage) ||
    isStudioLikeBooking(selectedOption?.bookingMode) ||
    selectedOption?.bookingMode === "mobile";
  const voucherActive = isHoffmannVoucherActive(user, selectedOption);
  const selectedOptionId = selectedOption?.id;
  const isBusinessAudienceOption = audienceForOption(selectedOption) === AUDIENCE_BUSINESS;
  const showOnlineSavingsBadge = !isBusinessAudienceOption;
  const requiresPaymentChoiceBase =
    isPaidBooking && !voucherActive && !selectedOption?.paymentLocked;

  const hasDetailsStep =
    selectedOption?.bookingMode === "consultation" ||
    requiresOnsiteAddress ||
    isStudioLikeBooking(selectedOption?.bookingMode);
  const paidBasePriceCents = useMemo(() => {
    if (!isPaidBooking || !selectedOption) return 0;
    if (isBusinessEventOption(selectedOption)) {
      return (
        Number(selectedOption.unitPriceCents || selectedOption.priceCents || 0) *
        selectedPersonCount
      );
    }
    if (selectedOption.priceCents && !isPremiumBookingOption(selectedOption)) {
      return selectedOption.priceCents;
    }
    if (isPremiumBookingOption(selectedOption) && !selectedDurationMinutes) {
      return selectedOption.priceCents || 7000;
    }
    const service = DR_HOFFMANN_SERVICES.find(
      (s) => s.key === selectedOption.serviceKey
    );
    return (
      individualServicePriceForPaymentMethod(
        selectedOption.serviceKey,
        effectivePaymentMethod,
        selectedDurationMinutes
      ) ??
      service?.durations.find(
        (duration) =>
          Number(duration.minutes) === Number(selectedDurationMinutes)
      )?.priceCents ??
      service?.durations[0]?.priceCents ??
      0
    );
  }, [isPaidBooking, selectedOption, selectedPersonCount, selectedDurationMinutes, effectivePaymentMethod]);

  const fixedGrossPricePreview = useMemo(() => {
    if (!isFixedGrossIndividualOption(selectedOption)) return null;
    if (selectedOption?.requiresVoucherQuestion && hasVoucher === null) {
      return null;
    }
    if (showDurationChoice && !selectedDurationMinutes) {
      return null;
    }
    return buildLocalPricePreview(
      selectedOption,
      selectedPersonCount,
      Boolean(hasVoucher),
      true,
      effectivePaymentMethod,
      pricingUser,
      selectedDurationMinutes
    );
  }, [selectedOption, selectedPersonCount, selectedDurationMinutes, showDurationChoice, hasVoucher, effectivePaymentMethod, pricingUser]);
  const currentPricePreview = fixedGrossPricePreview || pricePreview;

  const effectivePriceCents = isPaidBooking
    ? durationChoiceMissing
      ? null
      : currentPricePreview?.grossCents
      ? currentPricePreview.grossCents
      : voucherActive
      ? 0
      : priceForPaymentMethod(paidBasePriceCents, effectivePaymentMethod, {
          serviceKey: selectedOption?.serviceKey,
          user: pricingUser,
          durationMinutes: selectedDurationMinutes,
        })
    : 0;
  const referralBalanceCents = Math.max(0, Number(user?.referralBalanceCents || 0));
  const canUseReferralBalance =
    Boolean(user) &&
    referralBalanceCents > 0 &&
    isPaidBooking &&
    !voucherActive &&
    isReferralBalanceEligibleOption(selectedOption);
  const grossBeforeReferralCents =
    currentPricePreview?.grossCents ?? effectivePriceCents;
  const referralCreditPreviewCents =
    canUseReferralBalance && applyReferralBalance
      ? Math.min(referralBalanceCents, Math.max(0, grossBeforeReferralCents))
      : 0;
  const payableAfterReferralCents = Math.max(
    0,
    grossBeforeReferralCents - referralCreditPreviewCents
  );
  const isFreeDueToReferralBalance =
    applyReferralBalance &&
    referralCreditPreviewCents > 0 &&
    payableAfterReferralCents === 0;
  const showPaymentMethodChoice =
    requiresPaymentChoiceBase && payableAfterReferralCents > 0;
  const showPaymentStep =
    isPaidBooking &&
    !voucherActive &&
    grossBeforeReferralCents > 0 &&
    (showPaymentMethodChoice || canUseReferralBalance);
  const summaryStepNumber = showPaymentStep
    ? hasDetailsStep
      ? "04"
      : "03"
    : hasDetailsStep
      ? "03"
      : "02";
  const packageVatCents = selectedOption?.priceNetCents
    ? selectedOption.priceCents - selectedOption.priceNetCents
    : 0;

  const allowedPostalCodes = useMemo(() => {
    const codes = postalRules?.allowedPostalCodes || postalRules?.allowedPrefixes;
    return new Set(Array.isArray(codes) ? codes : []);
  }, [postalRules]);

  const plzIsValid =
    /^\d{5}$/.test(address.postalCode) && allowedPostalCodes.has(address.postalCode);

  const personCountChoices = useMemo(() => {
    if (!selectedOption?.personCountMax) return [];
    const min = Number(selectedOption.personCountMin || 1);
    const max = Number(selectedOption.personCountMax || min);
    return Array.from({ length: Math.max(0, max - min + 1) }, (_, idx) => min + idx);
  }, [selectedOption]);
  const usesPersonCountCounter = isWakeupPlusOption(selectedOption);
  const showPersonCount =
    usesPersonCountCounter ||
    personCountChoices.length > 0 ||
    Boolean(selectedOption?.personCountDefault);
  const personCountLabel = isWakeupPlusOption(selectedOption)
    ? "Mitarbeitende"
    : isBusinessEventOption(selectedOption)
      ? "Plätze"
      : "Personen";
  const updatePersonCount = useCallback(
    (next) => {
      const parsed = Math.round(Number(next));
      const fallback = defaultPersonCountForOption(selectedOption);
      const upper = personCountMax ?? Infinity;
      const bounded = Math.min(
        upper,
        Math.max(personCountMin, Number.isFinite(parsed) ? parsed : fallback)
      );
      setPersonCount(bounded);
    },
    [personCountMax, personCountMin, selectedOption]
  );
  const renderPersonCountInput = useCallback(
    (id) =>
      usesPersonCountCounter ? (
        <PersonCountCounter
          id={id}
          value={selectedPersonCount}
          min={personCountMin}
          max={personCountMax ?? undefined}
          onChange={updatePersonCount}
        />
      ) : (
        <select
          id={id}
          value={selectedPersonCount}
          onChange={(e) => updatePersonCount(e.target.value)}
        >
          {personCountChoices.map((count) => (
            <option key={count} value={count}>
              {count}
            </option>
          ))}
        </select>
      ),
    [
      personCountChoices,
      personCountMax,
      personCountMin,
      selectedPersonCount,
      updatePersonCount,
      usesPersonCountCounter,
    ]
  );

  const persistDraft = useCallback(() => {
    if (!selectedOption) return;
    saveBookingDraft(
      draftFromState({
        selectedOption,
        selectedDate,
        selectedSlot,
        consultationNotes,
        address,
        serviceAreaCheck,
        paymentMethod: effectivePaymentMethod,
        personCount,
        durationMinutes: selectedDurationMinutes,
        hasVoucher,
        voucherTermsAccepted,
        bookingPhone,
        wakeupComesToYou,
      })
    );
  }, [
    selectedOption,
    selectedDate,
    selectedSlot,
    consultationNotes,
    address,
    serviceAreaCheck,
    effectivePaymentMethod,
    personCount,
    selectedDurationMinutes,
    hasVoucher,
    voucherTermsAccepted,
    bookingPhone,
    wakeupComesToYou,
  ]);

  const scrollToOptionDetail = useCallback(() => {
    const target = optionDetailRef.current;
    if (!target) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, []);

  useEffect(() => {
    if (restoredDraftRef.current) return;
    restoredDraftRef.current = true;
    const draft = readBookingDraft();
    if (!draft) return;

    const normalizedDraftOptionId = normalizeOptionId(draft.optionId);
    const draftWakeupComesToYou = Boolean(
      draft.wakeupComesToYou || wakeupComesToYouFromId(draft.optionId)
    );
    const option =
      DELIVERY_STUDIO_OPTIONS[normalizedDraftOptionId]
        ? optionForDelivery(normalizedDraftOptionId, draftWakeupComesToYou)
        : ALL_OPTIONS.find((o) => o.id === normalizedDraftOptionId) ||
          initialOption;
    if (option) {
      setSelectedOption(option);
      setBookingAudience(audienceForOption(option));
      if (isDeliveryChoiceOption(option)) setWakeupComesToYou(draftWakeupComesToYou);
    }
    if (draft.selectedDate) {
      const [year, month, day] = String(draft.selectedDate).split("-").map(Number);
      if (year && month && day) setSelectedDate(new Date(year, month - 1, day));
    }
    if (draft.selectedSlot) setSelectedSlot(draft.selectedSlot);
    if (typeof draft.consultationNotes === "string") {
      setConsultationNotes(draft.consultationNotes);
    }
    if (draft.address && typeof draft.address === "object") {
      setAddress((current) => ({ ...current, ...draft.address }));
    }
    if (draft.serviceAreaCheck && typeof draft.serviceAreaCheck === "object") {
      setServiceAreaCheck((current) => ({ ...current, ...draft.serviceAreaCheck }));
    }
    if (draft.paymentMethod) {
      setPaymentMethod(paymentMethodForOption(option, draft.paymentMethod));
    }
    if (draft.personCount) setPersonCount(Number(draft.personCount));
    if (draft.durationMinutes) setDurationMinutes(Number(draft.durationMinutes));
    if (draft.hasVoucher === true || draft.hasVoucher === false) {
      setHasVoucher(draft.hasVoucher);
    }
    setVoucherTermsAccepted(Boolean(draft.voucherTermsAccepted));
    if (draft.bookingPhone) setBookingPhone(draft.bookingPhone);
  }, [initialOption]);

  useEffect(() => {
    if (!selectedOptionId) return;
    window.requestAnimationFrame(scrollToOptionDetail);
  }, [selectedOptionId, scrollToOptionDetail]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!cancelled && data?.user) dispatch(setUser({ user: data.user }));
      })
      .catch(() => {
        if (!cancelled) dispatch(setLogout());
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, token]);

  useEffect(() => {
    if (!selectedOption?.offerType) {
      setPricePreview(null);
      setLoadingPricePreview(false);
      return undefined;
    }
    if (isBusinessEventOption(selectedOption)) {
      setPricePreview(
        buildLocalPricePreview(
          selectedOption,
          selectedPersonCount,
          false,
          true,
          effectivePaymentMethod,
          pricingUser
        )
      );
      setLoadingPricePreview(false);
      return undefined;
    }
    if (selectedOption.requiresVoucherQuestion && hasVoucher === null) {
      setPricePreview(null);
      setLoadingPricePreview(false);
      return undefined;
    }
    if (showDurationChoice && !selectedDurationMinutes) {
      setPricePreview(null);
      setLoadingPricePreview(false);
      return undefined;
    }
    const localPreview = buildLocalPricePreview(
      selectedOption,
      selectedPersonCount,
      Boolean(hasVoucher),
      true,
      effectivePaymentMethod,
      pricingUser,
      selectedDurationMinutes
    );
    setPricePreview(localPreview);
    let cancelled = false;
    setLoadingPricePreview(true);

    api
      .post("/bookings/price-preview", {
        brand: PRICE_PREVIEW_BRAND,
        serviceKey: selectedOption.serviceKey,
        offerType: selectedOption.offerType,
        personCount: selectedPersonCount,
        durationMinutes: selectedDurationMinutes,
        paymentMethod: effectivePaymentMethod,
        ...(selectedOption.requiresVoucherQuestion
          ? { hasVoucher: Boolean(hasVoucher) }
          : {}),
      })
      .then(({ data }) => {
        const backendPreview =
          isFixedGrossIndividualOption(selectedOption) ||
          isWakeupPlusOption(selectedOption)
          ? localPreview
          : normalizePricePreview(data);
        if (!cancelled && backendPreview) setPricePreview(backendPreview);
      })
      .catch(() => {
        // Backend is the source of truth. This client formula is only a temporary
        // display fallback until the shared API exposes price-preview everywhere.
        if (!cancelled) setPricePreview(localPreview);
      })
      .finally(() => {
        if (!cancelled) setLoadingPricePreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOption, selectedPersonCount, selectedDurationMinutes, showDurationChoice, hasVoucher, effectivePaymentMethod, pricingUser]);

  useEffect(() => {
    if (!requiresServiceAreaCheck || selectedFullAddress.length < 3) {
      setAddressSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .get("/service-area/address-suggestions", {
          params: {
            brand: PRICE_PREVIEW_BRAND,
            q: selectedFullAddress,
          },
        })
        .then(({ data }) => {
          if (cancelled) return;
          const suggestions = Array.isArray(data?.suggestions)
            ? data.suggestions
            : Array.isArray(data)
              ? data
              : [];
          setAddressSuggestions(suggestions.slice(0, 5));
        })
        .catch(() => {
          if (!cancelled) setAddressSuggestions([]);
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [requiresServiceAreaCheck, selectedFullAddress]);

  // Lazy-load postal rules whenever the user opens the mobile flow.
  useEffect(() => {
    if (selectedOption?.bookingMode !== "mobile") return undefined;
    if (selectedOption?.skipPostalCheck) return undefined;
    if (postalRules) return undefined;
    let cancelled = false;
    api
      .get("/bookings/postal-rules")
      .then(({ data }) => {
        if (!cancelled && (data?.allowedPostalCodes || data?.allowedPrefixes)) {
          setPostalRules(data);
        }
      })
      .catch(() => {
        if (!cancelled) setPostalRules({ allowedPostalCodes: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOption, postalRules]);

  // Reload slots when the date or option changes.
  useEffect(() => {
    if (appointmentIsArranged) {
      setSlots([]);
      setSelectedSlot(null);
      return undefined;
    }
    if (!selectedOption || !selectedDate) {
      setSlots([]);
      setAvailabilityError(null);
      return undefined;
    }
    if (showDurationChoice && !selectedDurationMinutes) {
      setSlots([]);
      setSelectedSlot(null);
      setAvailabilityError("Bitte wähle zuerst eine Dauer aus.");
      return undefined;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setAvailabilityError(null);
    setSelectedSlot(null);
    const availabilityDuration = isWakeupBookingOption(selectedOption)
      ? wakeupServiceDurationMinutes(selectedOption, selectedPersonCount)
      : selectedDurationMinutes;
    const availabilityPersonCount = isWakeupPlusOption(selectedOption)
      ? Math.min(selectedPersonCount, 10)
      : selectedPersonCount;
    const availabilityParams = {
      date: toISODate(selectedDate),
      durationMinutes: availabilityDuration,
      brand: "dr-hoffmann",
      serviceKey: availabilityServiceKeyForOption(selectedOption),
      minimumLeadMinutes: selectedOption.minimumLeadMinutes,
    };
    const slotStepMinutes = slotStepMinutesForOption(selectedOption);
    if (slotStepMinutes) {
      availabilityParams.slotStepMinutes = slotStepMinutes;
    }
    if (isWakeupBookingOption(selectedOption)) {
      availabilityParams.personCount = availabilityPersonCount;
    }
    if (selectedOption.hoffmannBookingOption) {
      availabilityParams.hoffmannBookingOption = selectedOption.hoffmannBookingOption;
    }
    if (
      selectedOption.bookingMode === "studio-weekday" &&
      !usesDailyHoffmannSchedule(selectedOption)
    ) {
      availabilityParams.studioSchedule = "weekday";
      availabilityParams.location = studioLocationForOption(
        selectedOption,
        selectedDate
      );
    } else if (selectedOption.bookingMode === "studio-weekday") {
      availabilityParams.studioSchedule = studioScheduleForOption(
        selectedOption,
        selectedDate
      );
      availabilityParams.location = studioLocationForOption(
        selectedOption,
        selectedDate
      );
    } else if (selectedOption.bookingMode === "studio") {
      availabilityParams.studioSchedule = "thursday";
      availabilityParams.location = studioLocationForOption(
        selectedOption,
        selectedDate
      );
    }
    api
      .get("/bookings/availability", {
        params: availabilityParams,
      })
      .then(({ data }) => {
        if (cancelled) return;
        const rawSlots = Array.isArray(data?.slots) ? data.slots : [];
        setSlots(filterSlotsForOption(selectedOption, selectedDate, rawSlots));
      })
      .catch((err) => {
        if (!cancelled) {
          const responseMessage = String(err?.response?.data?.message || "").toLowerCase();
          let message = null;
          if (/2 stunden|mindestens.*2 stunden|2h|2 h|minimum.*2 stunden/i.test(responseMessage)) {
            message = `Bitte wähle einen Termin mit mindestens ${
              selectedOption.minimumLeadMinutes || 120
            } Minuten Vorlauf.`;
          } else if (/10:00|22:00|zwischen 10:00 und 22:00/i.test(responseMessage)) {
            message = "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr.";
          } else if (/12:00|18:00|19:00|19:30|20:00|zwischen 12:00 und 19:30|zwischen 12:00 und 19:00|zwischen 12:00 und 20:00|startzeit/i.test(responseMessage)) {
            message = usesIndividualSchedule(selectedOption)
              ? "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr."
              : "Bitte w\u00e4hle eine Startzeit zwischen 12:00 und 19:30 Uhr.";
          }
          setAvailabilityError(
            message ||
              "Wir konnten keine passenden Termine laden. Bitte wähle einen späteren Zeitpunkt."
          );
          setSlots([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentIsArranged, selectedDate, selectedOption, selectedPersonCount, selectedDurationMinutes, showDurationChoice, requiresServiceAreaCheck]);

  const handleSelectOption = (option) => {
    const nextOption = isDeliveryChoiceOption(option)
      ? optionForDelivery(option, false)
      : option;
    if (isBusinessEventOption(nextOption) && isAfterBusinessEventCutoff()) {
      setError(
        "Die Buchung ist nur bis Sonntag, 14.06.2026 um 16:00 Uhr möglich."
      );
      setErrorAction(null);
      return;
    }
    setBookingAudience(
      isBusinessEventOption(nextOption)
        ? bookingAudience
        : audienceForOption(nextOption)
    );
    if (option.specialRequestPath) {
      navigate(option.specialRequestPath);
      return;
    }
    if (option.externalUrl) {
      window.open(option.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setSelectedOption(nextOption);
    if (isDeliveryChoiceOption(nextOption)) setWakeupComesToYou(false);
    setSelectedDate(null);
    setSelectedSlot(null);
    setPaymentMethod(defaultPaymentForOption(nextOption));
    setPersonCount(defaultPersonCountForOption(nextOption));
    setDurationMinutes(initialDurationMinutesForOption(nextOption));
    setHasVoucher(nextOption.requiresVoucherQuestion ? null : false);
    setVoucherTermsAccepted(false);
    setBookingConsents(initialBookingConsents);
    setServiceAreaCheck({ status: "idle", message: "" });
    lastCheckedAddressRef.current = "";
    setAddressSuggestions([]);
    setError(null);
    setErrorAction(null);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("option", nextOption.id);
    nextParams.set("audience", audienceForOption(nextOption));
    navigate(`/buchen?${nextParams.toString()}`, { replace: true });
    window.setTimeout(scrollToOptionDetail, 0);
  };

  const handleWakeupDeliveryChange = (comesToYou) => {
    const nextOption = optionForDelivery(selectedOption, comesToYou);
    if (!nextOption) return;
    setWakeupComesToYou(comesToYou);
    setSelectedOption(nextOption);
    setSelectedDate(null);
    setSelectedSlot(null);
    setPaymentMethod(defaultPaymentForOption(nextOption));
    setPersonCount(defaultPersonCountForOption(nextOption));
    setDurationMinutes(initialDurationMinutesForOption(nextOption));
    setHasVoucher(nextOption.requiresVoucherQuestion ? null : false);
    setVoucherTermsAccepted(false);
    setBookingConsents(initialBookingConsents);
    setServiceAreaCheck({ status: "idle", message: "" });
    lastCheckedAddressRef.current = "";
    setAddressSuggestions([]);
    setError(null);
    setErrorAction(null);
  };

  const handleAudienceChange = (audienceId) => {
    setBookingAudience(audienceId);
    const nextParams = new URLSearchParams(location.search);
    if (selectedOption && audienceForOption(selectedOption) !== audienceId) {
      setSelectedOption(null);
      setSelectedDate(null);
      setSelectedSlot(null);
      setError(null);
      setErrorAction(null);
      nextParams.delete("option");
    }
    if (audienceId === AUDIENCE_BUSINESS) {
      nextParams.set("audience", audienceId);
    } else {
      nextParams.delete("audience");
    }
    const query = nextParams.toString();
    navigate(query ? `/buchen?${query}` : "/buchen", { replace: true });
  };

  const visibleOptions = (
    bookingAudience === AUDIENCE_BUSINESS
      ? [...BUSINESS_OPTIONS, ...LEGACY_OPTIONS]
      : [
          WAKEUP_STUDIO_OPTION,
          CLASSIC_STUDIO_OPTION,
          PREMIUM_STUDIO_OPTION,
          OPTIONS.find((option) => option.id === "studio"),
        ].filter(Boolean)
  ).filter(
    (option) => !isBusinessEventOption(option) || !isAfterBusinessEventCutoff()
  );
  const activeAudience =
    BOOKING_AUDIENCES.find((audience) => audience.id === bookingAudience) ||
    BOOKING_AUDIENCES[0];

  const updateAddress = (key) => (e) => {
    const value = e.target.value;
    setAddress((prev) => ({ ...prev, [key]: value }));
    if (
      requiresServiceAreaCheck &&
      ["fullAddress", "street", "postalCode", "city"].includes(key)
    ) {
      setServiceAreaCheck({ status: "idle", message: "" });
      lastCheckedAddressRef.current = "";
    }
  };

  const applyAddressSuggestion = (suggestion) => {
    const value =
      typeof suggestion === "string"
        ? suggestion
        : suggestion?.label || suggestion?.address || suggestion?.fullAddress || "";
    if (!value) return;
    setAddress((prev) => ({ ...prev, fullAddress: value }));
    setAddressSuggestions([]);
    setServiceAreaCheck({ status: "idle", message: "" });
    lastCheckedAddressRef.current = "";
    if (requiresServiceAreaCheck) {
      checkServiceArea(value).then((result) => {
        if (result !== null) lastCheckedAddressRef.current = value.trim();
      });
    }
  };

  const checkServiceArea = useCallback(
    async (addressOverride) => {
    if (!selectedOption || !requiresServiceAreaCheck) return null;
    const addressToCheck = String(addressOverride ?? selectedFullAddress).trim();
    if (!addressToCheck) {
      setServiceAreaCheck({
        status: "needs-address",
        message: "Bitte gib zuerst eine Adresse ein.",
      });
      return null;
    }
    setServiceAreaCheck({
      status: "checking",
      message: "Adresse wird geprüft ...",
    });
    try {
      const { data } = await api.post("/service-area/check", {
        brand: PRICE_PREVIEW_BRAND,
        serviceKey: selectedOption.serviceKey,
        offerType: selectedOption.offerType,
        address: addressToCheck,
      });
      if (data?.eligible) {
        const next = {
          status: "eligible",
          message: "Adresse liegt im Hamburger Stadtgebiet. Du kannst jetzt buchen.",
          serviceAreaEligible: true,
          serviceAreaVersion: data.serviceAreaVersion || data.areaVersion,
          serviceAreaCheckStatus: "passed",
          serviceAreaReason: data.reason || "",
          geocodedAddress: data.normalizedAddress || data.geocodedAddress || data.address,
        };
        setServiceAreaCheck(next);
        return next;
      }
      const next = {
        status:
          data?.status === "address-not-found" ||
          data?.status === "geocoding-unavailable"
            ? "unknown"
            : "outside",
        message:
          data?.status === "address-not-found" ||
          data?.status === "geocoding-unavailable"
            ? "Wir konnten die Adresse nicht sicher prüfen."
            : "Diese Adresse liegt außerhalb des Hamburger Stadtgebiets.",
        serviceAreaEligible: false,
        serviceAreaVersion: data?.serviceAreaVersion || data?.areaVersion,
        serviceAreaCheckStatus: data?.status || "not-eligible",
        serviceAreaReason: data?.reason || "",
      };
      setServiceAreaCheck(next);
      return next;
    } catch {
      const next = {
        status: "unknown",
        message: "Wir konnten die Adresse nicht sicher prüfen.",
        serviceAreaEligible: false,
        serviceAreaCheckStatus: "geocoding-unavailable",
      };
      setServiceAreaCheck(next);
      return next;
    }
  },
    [requiresServiceAreaCheck, selectedFullAddress, selectedOption]
  );

  const handleFullAddressBlur = useCallback(() => {
    if (!requiresServiceAreaCheck) return;
    const trimmed = selectedFullAddress.trim();
    if (!trimmed) {
      lastCheckedAddressRef.current = "";
      return;
    }
    if (trimmed === lastCheckedAddressRef.current) return;
    checkServiceArea().then((result) => {
      if (result !== null) lastCheckedAddressRef.current = trimmed;
    });
  }, [requiresServiceAreaCheck, selectedFullAddress, checkServiceArea]);

  const submitAuthedBooking = async () => {
    setError(null);
    setErrorAction(null);
    if (businessEventBookingClosed) {
      return setError(
        "Die Buchung ist nur bis Sonntag, 14.06.2026 um 16:00 Uhr möglich."
      );
    }
    if (!selectedSlot && !appointmentIsArranged) {
      return setError("Bitte wähle einen Termin aus.");
    }
    if (
      isLicensePartnerOption(selectedOption) &&
      startOfDay(selectedDate || new Date(selectedSlot)) < selectedMinDate
    ) {
      return setError(
        "Lizenzpartner-Termine sind erst zwei Werktage sp\u00e4ter buchbar, damit wir uns vorbereiten k\u00f6nnen."
      );
    }
    if (needsBookingPhone && !String(bookingPhone || "").trim()) {
      return setError("Bitte gib eine Telefonnummer an, damit wir dich notfalls erreichen k\u00f6nnen.");
    }
    if (voucherChoiceMissing) {
      return setError("Bitte wähle aus, ob du einen Gutschein hast.");
    }
    if (durationChoiceMissing) {
      return setError("Bitte wähle eine Dauer aus.");
    }
    if (hasVoucher === true && !voucherTermsAccepted) {
      return setError("Bitte bestätige, dass du den Gutschein zum Termin mitbringst.");
    }
    if (!bookingConsents.legalTerms) {
      return setError(
        "Bitte bestätige AGB, Datenschutzhinweise und Widerrufsbelehrung vor der verbindlichen Buchung."
      );
    }
    if (selectedOption.bookingMode === "mobile") {
      if (selectedOption.usesLegacyAddressFields) {
        if (!address.street || !address.postalCode || !address.city) {
          return setError("Bitte gib die vollständige Adresse an.");
        }
      } else if (!selectedFullAddress) {
        return setError("Bitte gib die vollständige Adresse an.");
      }
      if (requiresServiceAreaCheck && !addressIsEligible) {
        return setError("Bitte prüfe zuerst deine Adresse.");
      }
      if (!selectedOption.skipPostalCheck && !plzIsValid) {
        return setError(
          "Diese Postleitzahl liegt außerhalb des Hamburger Stadtgebiets."
        );
      }
    }
    if (!user || !token) {
      persistDraft();
      return setError(
        "Bitte melde dich an oder registriere dich kostenlos, um die Buchung abzuschließen."
      );
    }
    setSubmitting(true);
    try {
      const isConsultation = selectedOption.bookingMode === "consultation";
      const isWakeup = isWakeupBookingOption(selectedOption);
      const serviceDuration = wakeupServiceDurationMinutes(
        selectedOption,
        selectedPersonCount
      );
      const body = {
        serviceKey: selectedOption.serviceKey,
        paymentMethod:
          isConsultation && !selectedOption.paidPackage
            ? "later"
            : requiresStripeOnlyPayment(selectedOption)
              ? "stripe"
              : effectivePaymentMethod,
      };
      body.durationMinutes = isWakeupPlusOption(selectedOption)
        ? selectedOption.durationMinutes
        : isWakeupBookingOption(selectedOption)
          ? serviceDuration
          : selectedDurationMinutes;
      if (isWakeup) {
        body.serviceDurationMinutes = serviceDuration;
      }
      if (selectedOption.offerType) {
        body.offerType = selectedOption.offerType;
      }
      if (selectedOption.minimumLeadMinutes) {
        body.minimumLeadMinutes = selectedOption.minimumLeadMinutes;
      }
      if (selectedOption.personCountDefault || selectedOption.personCountMax) {
        body.personCount = selectedPersonCount;
      }
      if (selectedOption.requiresVoucherTerms) {
        body.voucherTermsAccepted = Boolean(voucherTermsAccepted);
      }
      if (selectedOption.requiresVoucherQuestion) {
        body.hasVoucher = Boolean(hasVoucher);
        body.voucherTermsAccepted = Boolean(hasVoucher && voucherTermsAccepted);
        body.requiresVoucherPresentation = Boolean(hasVoucher);
      }
      if (selectedSlot) {
        body.startISO = selectedSlot;
      } else if (isBusinessEventOption(selectedOption)) {
        body.startISO = HOFFMANN_BUSINESS_EVENT_START_ISO;
      } else if (appointmentIsArranged) {
        body.startISO = arrangedAppointmentStartISO();
      }
      if (needsBookingPhone) {
        body.phone = bookingPhone.trim();
      }
      if (isBusinessEventOption(selectedOption)) {
        body.priceCents = paidBasePriceCents;
        body.location = selectedOption.eventLocation || HOFFMANN_BUSINESS_EVENT_LOCATION;
        body.hoffmannBookingOption =
          selectedOption.hoffmannBookingOption || selectedOption.title;
      } else if (selectedOption.paidPackage && selectedOption.priceCents) {
        body.priceCents = selectedOption.priceCents;
      }
      if (
        isFixedGrossIndividualOption(selectedOption) &&
        currentPricePreview?.grossCents
      ) {
        body.priceCents = currentPricePreview.grossCents;
      }
      if (voucherActive && selectedOption.voucherType) {
        body.voucherType = selectedOption.voucherType;
        body.applyFirstBookingVoucher = true;
      }
      if (isConsultation) {
        const notes = [
          appointmentIsArranged
            ? "Termin und Uhrzeit werden telefonisch abgestimmt."
            : "",
          consultationNotes.trim(),
        ]
          .filter(Boolean)
          .join("\n");
        if (notes) body.notes = notes;
      }
      if (isConsultation) {
        body.hoffmannBookingOption =
          selectedOption.hoffmannBookingOption || selectedOption.title;
      }
      if (isStudioLikeBooking(selectedOption.bookingMode)) {
        const studioSchedule = studioScheduleForOption(
          selectedOption,
          selectedSlot || selectedDate
        );
        body.location = studioLocationForOption(
          selectedOption,
          selectedSlot || selectedDate
        );
        body.studioSchedule = studioSchedule;
        body.studioScheduleWeekday =
          selectedOption.bookingMode === "studio-weekday" &&
          (!usesDailyHoffmannSchedule(selectedOption) ||
            studioSchedule === "thursday");
        body.hoffmannBookingOption = selectedOption.title;
      }
      if (selectedOption.bookingMode === "mobile") {
        body.serviceAddress = buildServiceAddressForOption(selectedOption, {
          ...address,
          geocodedAddress: requiresServiceAreaCheck
            ? serviceAreaCheck.geocodedAddress
            : "",
        });
        if (selectedFullAddress) {
          body.address = selectedFullAddress;
        }
        body.hoffmannBookingOption = selectedOption.title;
        if (requiresServiceAreaCheck) {
          body.serviceAreaEligible = serviceAreaCheck.serviceAreaEligible;
          body.serviceAreaVersion = serviceAreaCheck.serviceAreaVersion;
          body.serviceAreaCheckStatus =
            serviceAreaCheck.serviceAreaEligible === true
              ? "passed"
              : serviceAreaCheck.serviceAreaCheckStatus;
          body.serviceAreaReason = serviceAreaCheck.serviceAreaReason;
          body.geocodedAddress = serviceAreaCheck.geocodedAddress;
        }
      }
      if (canUseReferralBalance) {
        body.applyReferralBalance = applyReferralBalance;
      }
      body.consents = {
        termsAccepted: bookingConsents.legalTerms,
        privacyNoticeRead: bookingConsents.legalTerms,
        withdrawalInstructionAccepted: bookingConsents.legalTerms,
        capturedAt: new Date().toISOString(),
        source: "dr-hoffmann-booking",
      };
      if (process.env.NODE_ENV === "development") {
        console.debug("Booking payload", {
          serviceKey: selectedOption.serviceKey,
          offerType: selectedOption.offerType,
          personCount: selectedPersonCount,
          hasStartISO: Boolean(body.startISO),
          hasPaymentMethod: Boolean(body.paymentMethod),
          hasServiceAddress: Boolean(body.serviceAddress),
          hasAddress: Boolean(body.address),
          areaCheckEligible:
            !requiresServiceAreaCheck || addressIsEligible,
          durationMinutes: body.durationMinutes,
          serviceDurationMinutes: body.serviceDurationMinutes,
        });
      }
      let data;
      try {
        ({ data } = await api.post("/bookings", body));
      } catch (bookingErr) {
        const responseMessage = String(
          bookingErr?.response?.data?.message || bookingErr?.message || ""
        );
        if (/missing_service_address/i.test(responseMessage)) {
          throw new Error(
            "Die geprüfte Adresse konnte nicht übernommen werden. Bitte prüfe deine Adresse erneut."
          );
        }
        if (/10:00|22:00|zwischen 10:00 und 22:00/i.test(responseMessage)) {
          throw new Error(
            "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr."
          );
        }
        if (/12:00|18:00|19:00|19:30|20:00|startzeit/i.test(responseMessage)) {
          throw new Error(
            usesIndividualSchedule(selectedOption)
              ? "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr."
              : "Bitte w\u00e4hle eine Startzeit zwischen 12:00 und 19:30 Uhr."
          );
        }
        if (/missing_required.*field|pflichtfeld/i.test(responseMessage)) {
          const missingFields = bookingErr?.response?.data?.missingFields || [];
          const fieldMap = {
            startISO: "Termin",
            paymentMethod: "Zahlungsart",
            serviceAddress: "Adresse",
            address: "Adresse",
            personCount: "Personenanzahl",
            serviceKey: "Angebot",
            offerType: "Angebotstyp",
          };
          const readableFields = missingFields
            .map((f) => fieldMap[f] || f)
            .join(", ");
          throw new Error(
            readableFields
              ? `Bitte prüfe deine Angaben. Es fehlen noch: ${readableFields}.`
              : "Bitte prüfe deine Angaben. Es fehlen noch erforderliche Felder."
          );
        }
        if (
          selectedOption.legacyServiceKey &&
          /leistung|service|angebot/i.test(responseMessage)
        ) {
          ({ data } = await api.post("/bookings", {
            ...body,
            serviceKey: selectedOption.legacyServiceKey,
          }));
        } else {
          throw bookingErr;
        }
      }
      const booking = data.booking;
      // Trust server price after Guthaben: fully covered bookings are confirmed
      // with priceCents 0 and must not open Stripe checkout.
      const needsStripeCheckout =
        booking?.status === "pending-payment" &&
        booking?.paymentMethod === "stripe" &&
        Number(booking?.priceCents) > 0;

      if (needsStripeCheckout) {
        try {
          const checkoutPriceCents = isBusinessEventOption(selectedOption)
            ? paidBasePriceCents
            : selectedOption.paidPackage && selectedOption.priceCents
              ? selectedOption.priceCents
              : isFixedGrossIndividualOption(selectedOption) &&
                  currentPricePreview?.grossCents
                ? currentPricePreview.grossCents
                : undefined;
          const { data: checkout } = await api.post(
            `/payment/checkout/${booking._id}`,
            checkoutPriceCents
              ? {
                  priceCents: checkoutPriceCents,
                  serviceLabel: selectedOption.title,
                  hoffmannBookingOption: selectedOption.hoffmannBookingOption,
                }
              : undefined
          );
          if (!checkout?.url) {
            throw new Error("Zahlungsseite konnte nicht geöffnet werden.");
          }
          clearBookingDraft();
          window.location.href = checkout.url;
          return;
        } catch (checkoutErr) {
          try {
            await api.delete(`/bookings/${booking._id}`);
          } catch {
            /* slot cleanup best-effort */
          }
          throw new Error(
            checkoutErr?.response?.data?.message ||
              checkoutErr?.message ||
              "Die Online-Zahlung f\u00fcr das Starterpaket konnte nicht gestartet werden. Die Buchung wurde nicht angelegt."
          );
        }
      }
      try {
        const { data: me } = await api.get("/auth/me");
        if (me?.user) dispatch(setUser({ user: me.user }));
      } catch {
        /* ignore */
      }
      clearBookingDraft();
      navigate(`/buchen/erfolg?bookingId=${booking._id}`, {
        state: { booking },
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "";
      if (err?.response?.status === 401) {
        dispatch(setLogout());
        persistDraft();
        const params = new URLSearchParams({
          next: `${location.pathname}${location.search}`,
        });
        navigate(`/einloggen?${params.toString()}`);
        return;
      }
      if (err?.response?.data?.requiresSpecialRequest) {
        const offer =
          selectedOption?.offerType === "wakeup_plus_business"
            ? "wakeup-plus"
            : "wakeup-onsite";
        setError(
          message ||
            "Diese Buchung braucht eine Sonderanfrage. Bitte nutze unser Kontaktformular."
        );
        setErrorAction({
          href: `/kontakt?reason=outside-area&offer=${offer}&address=${encodeURIComponent(selectedFullAddress)}`,
          label: "Sonderanfrage stellen",
        });
      } else if (
        selectedOption?.offerType &&
        /leistung|service|angebot/i.test(message)
      ) {
        setError(
          "Dieses Angebot ist gerade noch nicht verfügbar. Bitte stelle eine Anfrage über unser Kontaktformular."
        );
        setErrorAction({
          href: `/kontakt?reason=service-unavailable&offer=${selectedOption.id}&personCount=${selectedPersonCount}`,
          label: "Zum Kontaktformular",
        });
      } else {
        setError(message || "Die Buchung konnte nicht angelegt werden.");
      }
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  return (
    <Layout>
      <section className="booking-page">
        <h1 className="booking-title">
          Jetzt <em>Termin</em> buchen
        </h1>
        <p className="booking-intro">
          Wähle die passende Option und fülle die Buchung in Ruhe aus.
          Anmeldung oder Registrierung erfolgt erst beim Abschließen.
        </p>
        <div className="booking-audience-switch" role="tablist" aria-label="Buchungsart">
          {BOOKING_AUDIENCES.map((audience) => {
            const isActive = bookingAudience === audience.id;
            return (
              <button
                key={audience.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`booking-audience-tab ${isActive ? "is-active" : ""}`}
                onClick={() => handleAudienceChange(audience.id)}
              >
                {audience.label}
              </button>
            );
          })}
        </div>
        <div className="booking-audience-copy">
          <strong>{activeAudience.title}</strong>
          <span>{activeAudience.description}</span>
          <p>
            Du möchtest uns{" "}
            <Link to="/empfehlungsprogramm">weiterempfehlen</Link>
            {" "}und 5 € Massage-Guthaben erhalten?
          </p>
        </div>
        <div
          className={`option-grid option-grid--booking ${
            bookingAudience === AUDIENCE_INDIVIDUAL
              ? "option-grid--individual"
              : "option-grid--business"
          }`}
        >
          {visibleOptions.map((option) => {
            const isSelected = selectedOption?.id === option.id;
            const priceBeforeActions = BOTTOM_PRICE_OPTION_IDS.has(option.id);
            return (
              <div
                key={option.id}
                className={`option-card option-card--${option.id} ${isSelected ? "is-selected" : ""}`}
              >
                {option.spotlight && (
                  <div className="option-featured-badge">
                    {option.spotlight}
                  </div>
                )}
                <div className="option-card-content">
                  <h3>{option.title}</h3>
                  {option.subtitle && (
                    <p className="option-subtitle">{option.subtitle}</p>
                  )}
                  <p>
                    {option.highlight && (
                      <strong className="option-highlight-red">
                        {option.highlight}
                      </strong>
                    )}
                    {option.highlight ? " " : ""}
                    {option.description}
                  </p>
                  {option.features && (
                    <ul className="option-feature-list">
                      {option.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {priceBeforeActions ? (
                  <div className="option-before-price">
                    {option.hint && <p className="option-hint">{option.hint}</p>}
                  </div>
                ) : (
                  <>
                    {option.badge && (
                      <div className="option-badge">{option.badge}</div>
                    )}
                    {option.hint && <p className="option-hint">{option.hint}</p>}
                  </>
                )}
                {priceBeforeActions && <div className="option-card-spacer" />}
                {priceBeforeActions && option.badge && (
                  <div className="option-badge">{option.badge}</div>
                )}
                <OptionPriceDisplay option={option} user={user} />
                <div className="option-actions">
                  {option.externalUrl ? (
                    <>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => handleSelectOption(option)}
                      >
                        {option.externalCta || "Zur Buchung →"}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={isSelected ? "btn-outline" : "btn-primary"}
                      onClick={() => handleSelectOption(option)}
                    >
                      {isSelected ? "Auswahl aktiv" : option.cta || "Auswählen"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
          <div className="booking-special-request">
            <span>Sonderwünsche, Events oder größere Gruppen?</span>
            <Link to={SPECIAL_REQUEST_OPTION.specialRequestPath} className="btn-outline">
              {SPECIAL_REQUEST_OPTION.cta}
            </Link>
          </div>
        
        {selectedOption && (
          <div className="option-detail" ref={optionDetailRef}>
            <div className="booking-step">
              <div className="step-head">
                <h2>
                  {selectedOption.title} — <em>Datum &amp; Uhrzeit</em>
                </h2>
                <span className="booking-step-number">01</span>
              </div>
              {isBusinessEventOption(selectedOption) && (
                <div className="booking-summary-block" style={{ marginTop: "1rem", borderTop: "none", paddingTop: 0 }}>
                  <div className="summary-row">
                    <span>Datum &amp; Uhrzeit</span>
                    <strong>{HOFFMANN_BUSINESS_EVENT_SCHEDULE_LABEL}</strong>
                  </div>
                  <div className="summary-row">
                    <span>Ort</span>
                    <strong>
                      {selectedOption.eventLocation || HOFFMANN_BUSINESS_EVENT_LOCATION}
                    </strong>
                  </div>
                  {showPersonCount && (
                    <div className="address-grid" style={{ marginTop: "1rem" }}>
                      <div className="full">
                        <label htmlFor="person-count-event">{personCountLabel} *</label>
                        {renderPersonCountInput("person-count-event")}
                        <p className="field-note">
                          Maximal {HOFFMANN_BUSINESS_EVENT_MAX_SEATS_PER_USER} Plätze pro Person.
                        </p>
                      </div>
                      {needsBookingPhone && (
                        <div className="full">
                          <label htmlFor="booking-phone-event">Telefonnummer *</label>
                          <input
                            id="booking-phone-event"
                            type="tel"
                            required
                            autoComplete="tel"
                            placeholder="+49 ..."
                            value={bookingPhone}
                            onChange={(e) => setBookingPhone(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isDeliveryChoiceOption(selectedOption) && (
                <div className="booking-delivery-choice" aria-label={`${selectedOption.title} Ort`}>
                  <strong>Sollen wir zu dir kommen?</strong>
                  <div className="booking-delivery-options">
                    <label className={!wakeupComesToYou ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="wakeup-delivery"
                        checked={!wakeupComesToYou}
                        onChange={() => handleWakeupDeliveryChange(false)}
                      />
                      Nein, im Studio
                    </label>
                    <label className={wakeupComesToYou ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="wakeup-delivery"
                        checked={wakeupComesToYou}
                        onChange={() => handleWakeupDeliveryChange(true)}
                      />
                      <span>Ja, bitte vor Ort</span>
                      <span className="booking-delivery-badge">+ 39 € Anfahrt</span>
                    </label>
                  </div>
                  {selectedOption.requiresVoucherQuestion && (
                    <div className="voucher-question booking-delivery-voucher">
                      <span className="voucher-question__label">
                        Hast du einen Gutschein? *
                      </span>
                      <div className="voucher-question__choices">
                        <label>
                          <input
                            type="radio"
                            name="has-voucher"
                            checked={hasVoucher === true}
                            onChange={() => {
                              setHasVoucher(true);
                              setVoucherTermsAccepted(false);
                            }}
                          />
                          Ja
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="has-voucher"
                            checked={hasVoucher === false}
                            onChange={() => {
                              setHasVoucher(false);
                              setVoucherTermsAccepted(false);
                            }}
                          />
                          Nein
                        </label>
                      </div>
                      {hasVoucher === true && (
                        <>
                          <div className="booking-notice">
                            <strong>Gutschein bitte beim Termin vorzeigen.</strong>
                            <p>
                              Der Gutschein spart die Anfahrtskosten und ist nicht
                              kombinierbar.
                            </p>
                          </div>
                          <label className="booking-checkbox">
                            <input
                              type="checkbox"
                              checked={voucherTermsAccepted}
                              onChange={(e) =>
                                setVoucherTermsAccepted(e.target.checked)
                              }
                            />
                            <span>Ich bringe den Gutschein zum Termin mit.</span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              {showDurationChoice && (
                <div className="address-grid">
                  <div className="full">
                    <label htmlFor="duration-minutes">Dauer *</label>
                    <select
                      id="duration-minutes"
                      value={selectedDurationMinutes ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDurationMinutes(value ? Number(value) : "");
                      }}
                    >
                      <option value="" disabled>
                        Bitte wählen…
                      </option>
                      {durationChoices.map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {minutes} Min.
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {appointmentIsArranged && !isBusinessEventOption(selectedOption) ? (
                <div className="booking-summary-block" style={{ marginTop: "1rem", borderTop: "none", paddingTop: 0 }}>
                  <div className="summary-row">
                    <span>Datum</span>
                    <strong>Nach Vereinbarung</strong>
                  </div>
                  <div className="summary-row">
                    <span>Uhrzeit</span>
                    <strong>Nach Vereinbarung</strong>
                  </div>
                  <p className="step-summary" style={{ marginTop: "1rem" }}>
                    Wir stimmen den Termin für das Starterpaket telefonisch mit dir ab.
                  </p>
                </div>
              ) : !isBusinessEventOption(selectedOption) ? (
                <Calendar
                  selectedDate={selectedDate}
                  onSelect={setSelectedDate}
                  minDate={selectedMinDate}
                  maxDate={selectedMaxDate}
                  weekdaysOnly={
                    !isBusinessEventOption(selectedOption) &&
                    !isThursdayOnlyStudio(selectedOption) &&
                    !usesDailyHoffmannSchedule(selectedOption)
                  }
                  onlyWeekday={
                    isThursdayOnlyStudio(selectedOption) ? 4 : -1
                  }
                  closedWeekday={-1}
                  closedDayTitle={
                    isThursdayOnlyStudio(selectedOption)
                      ? "Studio-Massagen nur Donnerstags"
                      : undefined
                  }
                  disableDate={
                    isThursdayOnlyStudio(selectedOption)
                      ? isAfterHoffmannOption1Cutoff
                      : undefined
                  }
                  disableDateTitle="Studio Massagen sind nur bis Ende Juni buchbar"
                />
              ) : null}
              {isLicensePartnerOption(selectedOption) && (
                <p className="step-summary" style={{ marginTop: "1rem" }}>
                  {"Lizenzpartner-Termine sind frühestens zwei Werktage im Voraus buchbar, damit wir den Erstbesuch vorbereiten können."}
                </p>
              )}
              {selectedDate && !isBusinessEventOption(selectedOption) && (
                <>
                  {bookingHoursHintForOption(selectedOption, selectedDate) && (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {bookingHoursHintForOption(selectedOption, selectedDate)}
                    </p>
                  )}
                  {loadingSlots ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      Verfügbare Zeiten werden geladen…
                    </p>
                  ) : slots.length === 0 ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {availabilityError || "An diesem Tag sind keine passenden Termine mehr verfügbar. Bitte wähle einen späteren Zeitpunkt."}
                    </p>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((iso) => (
                        <button
                          type="button"
                          key={iso}
                          onClick={() => setSelectedSlot(iso)}
                          className={`slot-button ${selectedSlot === iso ? "is-selected" : ""}`}
                        >
                          {formatTime(iso)}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedOption.bookingMode === "consultation" && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    Deine <em>Hinweise</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <p className="step-summary">
                  Die Bestätigung geht an die E-Mail-Adresse deines Kontos. Optional kannst du uns hier noch etwas mitteilen.
                </p>
                <div className="address-grid">
                  {needsBookingPhone && (
                    <div className="full">
                      <label htmlFor="booking-phone-consultation">Telefonnummer *</label>
                      <input
                        id="booking-phone-consultation"
                        type="tel"
                        required
                        autoComplete="tel"
                        placeholder="+49 ..."
                        value={bookingPhone}
                        onChange={(e) => setBookingPhone(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="full">
                    <label htmlFor="cf-notes">Hinweise (optional)</label>
                    <textarea
                      id="cf-notes"
                      rows={3}
                      maxLength={500}
                      value={consultationNotes}
                      onChange={(e) => setConsultationNotes(e.target.value)}
                      placeholder="Standort, Wünsche, beste Erreichbarkeit…"
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedOption.bookingMode === "mobile" && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    Deine <em>Adresse</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <p className="step-summary">
                  {requiresServiceAreaCheck
                    ? "Vor-Ort-Termine sind im gesamten Hamburger Stadtgebiet buchbar. Die Adresse wird automatisch geprüft, sobald du das Feld verlässt."
                    : "Wir kommen zu dir. Buchbar im gesamten Hamburger Stadtgebiet."}
                </p>
                {requiresServiceAreaCheck && (
                  <p className="field-note" style={{ marginTop: "0.75rem" }}>
                    Für die Planung blocken wir je nach Personenanzahl den passenden Zeitraum.
                  </p>
                )}
                {/* ServiceAreaMap temporarily hidden until design is final */}
                <div className="address-grid">
                  {needsBookingPhone && (
                    <div className="full">
                      <label htmlFor="ad-phone">Telefonnummer *</label>
                      <input
                        id="ad-phone"
                        type="tel"
                        required
                        autoComplete="tel"
                        placeholder="+49 ..."
                        value={bookingPhone}
                        onChange={(e) => setBookingPhone(e.target.value)}
                      />
                    </div>
                  )}
                  {showPersonCount && (
                    <div className="full">
                      <label htmlFor="person-count-mobile">{personCountLabel} *</label>
                      {renderPersonCountInput("person-count-mobile")}
                      {isWakeupPlusOption(selectedOption) && (
                        <p className="field-note">
                          1-10 Mitarbeitende: 79 € netto zzgl. MwSt. Jede weitere Person: +5 € netto.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="full">
                    <label htmlFor="ad-company">Name / Firma *</label>
                    <input id="ad-company" value={address.companyName} onChange={updateAddress("companyName")} />
                  </div>
                  {selectedOption.usesLegacyAddressFields ? (
                    <>
                      <div className="full">
                        <label htmlFor="ad-street">Straße &amp; Hausnummer *</label>
                        <input id="ad-street" value={address.street} onChange={updateAddress("street")} />
                      </div>
                      <div>
                        <label htmlFor="ad-plz">PLZ *</label>
                        <input
                          id="ad-plz"
                          inputMode="numeric"
                          maxLength={5}
                          value={address.postalCode}
                          onChange={updateAddress("postalCode")}
                        />
                        {address.postalCode.length === 5 && (
                          <div className={`plz-status ${plzIsValid ? "is-ok" : "is-bad"}`}>
                            {plzIsValid
                              ? "PLZ liegt im Hamburger Stadtgebiet."
                              : "PLZ liegt außerhalb des Hamburger Stadtgebiets."}
                          </div>
                        )}
                      </div>
                      <div>
                        <label htmlFor="ad-city">Ort *</label>
                        <input id="ad-city" value={address.city} onChange={updateAddress("city")} />
                      </div>
                    </>
                  ) : (
                    <div className="full">
                      <label htmlFor="ad-full-address">Adresse *</label>
                      <input
                        id="ad-full-address"
                        value={address.fullAddress}
                        onChange={updateAddress("fullAddress")}
                        onBlur={requiresServiceAreaCheck ? handleFullAddressBlur : undefined}
                        placeholder="Straße Hausnummer, PLZ Ort"
                        autoComplete="street-address"
                      />
                      {addressSuggestions.length > 0 && (
                        <div className="address-suggestions">
                          {addressSuggestions.map((suggestion, idx) => {
                            const label =
                              typeof suggestion === "string"
                                ? suggestion
                                : suggestion?.label ||
                                  suggestion?.address ||
                                  suggestion?.fullAddress ||
                                  "";
                            if (!label) return null;
                            return (
                              <button
                                type="button"
                                key={`${label}-${idx}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => applyAddressSuggestion(suggestion)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {requiresServiceAreaCheck && (
                        <div className="service-area-check">
                          {serviceAreaCheck.message ? (
                            <div
                              className={`service-area-status is-${serviceAreaCheck.status}`}
                            >
                              {serviceAreaCheck.message}
                            </div>
                          ) : null}
                          {(serviceAreaCheck.status === "outside" ||
                            serviceAreaCheck.status === "unknown") && (
                            <Link
                              to={`/kontakt?reason=outside-area&offer=${isWakeupPlusOption(selectedOption) ? "wakeup-plus" : selectedOption.id}&address=${encodeURIComponent(selectedFullAddress)}`}
                              className="btn-ghost"
                            >
                              Sonderanfrage stellen
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="full">
                    <label htmlFor="ad-notes">Hinweise (optional)</label>
                    <textarea
                      id="ad-notes"
                      rows={3}
                      maxLength={300}
                      value={address.notes}
                      onChange={updateAddress("notes")}
                      placeholder="Stockwerk, Klingelschild, Hinweise zum Ablauf…"
                    />
                  </div>
                </div>
              </div>
            )}

            {isStudioLikeBooking(selectedOption.bookingMode) && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    <em>Ort</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <p className="step-summary">
                  
                </p>
                <div className="address-grid">
                  <div className="full">
                    <p className="step-summary" style={{ marginTop: 0 }}>
                      <strong>{selectedStudioLocation}</strong>
                    </p>
                  </div>
                  {showPersonCount && (
                    <div className="full">
                      <label htmlFor="person-count-studio">{personCountLabel} *</label>
                      {renderPersonCountInput("person-count-studio")}
                    </div>
                  )}
                  {needsBookingPhone && (
                    <div className="full">
                      <label htmlFor="booking-phone-studio">Telefonnummer *</label>
                      <input
                        id="booking-phone-studio"
                        type="tel"
                        required
                        autoComplete="tel"
                        placeholder="+49 ..."
                        value={bookingPhone}
                        onChange={(e) => setBookingPhone(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {showPaymentStep && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    <em>Zahlung</em>
                  </h2>
                  <span className="booking-step-number">
                    {hasDetailsStep ? "03" : "02"}
                  </span>
                </div>
                <p className="step-summary">
                  {canUseReferralBalance && !showPaymentMethodChoice
                    ? "Dein Guthaben deckt den gesamten Betrag."
                    : canUseReferralBalance && showPaymentMethodChoice
                      ? "Wähle die Zahlungsart und ob du Guthaben einlösen möchtest."
                      : onlinePaymentOnly
                    ? "Diese Buchung ist ausschließlich online zahlbar."
                    : isBusinessEventOption(selectedOption)
                      ? "Wähle, ob du online oder vor Ort bei der WM-Aktion bezahlen möchtest."
                      : "Wähle, ob du online oder vor Ort beim Termin bezahlen möchtest."}
                </p>
                {showPaymentMethodChoice && (
                <div className="payment-options">
                  <label
                    className={`payment-option ${onlinePaymentOnly || effectivePaymentMethod === "stripe" ? "is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="stripe"
                      checked={onlinePaymentOnly || effectivePaymentMethod === "stripe"}
                      onChange={() => setPaymentMethod("stripe")}
                    />
                    <div className="payment-option-info">
                      <strong className="payment-option-title">
                        Online bezahlen
                        {showOnlineSavingsBadge && (
                          <span className="payment-option-badge">
                            Zahle online und spare Geld
                          </span>
                        )}
                      </strong>
                      <span>
                        Nach dem Bestätigen leiten wir dich zu Stripe weiter.
                      </span>
                    </div>
                  </label>
                  {!onlinePaymentOnly && (
                  <label
                    className={`payment-option ${onsitePaymentDisabled ? "is-disabled" : ""} ${!onsitePaymentDisabled && effectivePaymentMethod === "on-site" ? "is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="on-site"
                      checked={!onsitePaymentDisabled && effectivePaymentMethod === "on-site"}
                      disabled={onsitePaymentDisabled}
                      onChange={() => setPaymentMethod("on-site")}
                    />
                    <div className="payment-option-info">
                      <strong>
                        {onsitePaymentDisabled ? "Vor Ort bezahlen?" : "Vor Ort bezahlen"}
                      </strong>
                      {onsitePaymentDisabled ? (
                        <>
                          <span className="payment-option-note">
                            Wenn du vor Ort bar zahlen möchtest, <b>zahlst du mehr</b>. Um das zu umgehen, kannst du dein Kontoguthaben{" "}
                            <Link to="/aufladen">hier aufladen</Link>.
                          </span>
                          <span className="payment-option-note">
                            Alternativ kannst du gerne zu uns zum Cremon 11, 20457 Hamburg kommen und dein Kontoguthaben mit Bargeld aufladen.
                          </span>
                        </>
                      ) : (
                        <span>
                          {isBusinessEventOption(selectedOption)
                            ? "Bar oder Karte direkt vor Ort bei der WM-Aktion."
                            : isStudioLikeBooking(selectedOption.bookingMode)
                            ? "Bar oder Karte direkt im Studio beim Termin."
                            : "Bar oder Karte beim Termin vor Ort bei dir."}
                        </span>
                      )}
                    </div>
                  </label>
                  )}
                </div>
                )}
                {canUseReferralBalance && (
                  <label className="referral-balance-option">
                    <input
                      type="checkbox"
                      checked={applyReferralBalance}
                      onChange={(e) => setApplyReferralBalance(e.target.checked)}
                    />
                    <span>
                      Guthaben verwenden ({formatPrice(referralBalanceCents)})
                    </span>
                  </label>
                )}
              </div>
            )}

            <div className="booking-step">
              <div className="step-head">
                <h2>
                  Deine <em>Buchung</em>
                </h2>
                <span className="booking-step-number">{summaryStepNumber}</span>
              </div>
              <div className="booking-summary-block" style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
                <div className="summary-row">
                  <span>Leistung</span>
                  <strong>{selectedOption.title}</strong>
                </div>
                {selectedOption.voucherType && (
                  <div className="summary-row">
                    <span>Kostenfreier Erstbesuch</span>
                    <strong>
                      {voucherActive
                        ? (
                          <span className="booking-free-visit-label">
                            Kostenfreier Erstbesuch
                          </span>
                        )
                        : "Bereits eingelöst"}
                    </strong>
                  </div>
                )}
                {!isBusinessEventOption(selectedOption) && (
                  <div className="summary-row">
                    <span>Dauer</span>
                    <strong>
                      {isWakeupBookingOption(selectedOption)
                        ? `${selectedPersonCount} × ${WAKEUP_MINUTES_PER_PERSON} Min.`
                        : durationChoiceMissing
                          ? "Bitte wählen"
                          : `${selectedDurationMinutes} Min.`}
                    </strong>
                  </div>
                )}
                {showPersonCount && (
                  <div className="summary-row">
                    <span>{personCountLabel}</span>
                    <strong>{selectedPersonCount}</strong>
                  </div>
                )}
                {isBusinessEventOption(selectedOption) ? (
                  <div className="summary-row">
                    <span>Datum &amp; Uhrzeit</span>
                    <strong>{HOFFMANN_BUSINESS_EVENT_SCHEDULE_LABEL}</strong>
                  </div>
                ) : (
                  <>
                    <div className="summary-row">
                      <span>Datum</span>
                      <strong>
                        {appointmentIsArranged
                          ? "Nach Vereinbarung"
                          : selectedDate
                            ? formatLong(selectedDate)
                            : "—"}
                      </strong>
                    </div>
                    <div className="summary-row">
                      <span>Uhrzeit</span>
                      <strong>
                        {appointmentIsArranged
                          ? "Nach Vereinbarung"
                          : selectedSlot
                            ? formatTime(selectedSlot)
                            : "—"}
                      </strong>
                    </div>
                  </>
                )}
                {selectedOption.bookingMode !== "mobile" && (
                  <div className="summary-row">
                    <span>Ort</span>
                    <strong>
                      {isBusinessEventOption(selectedOption)
                        ? selectedOption.eventLocation || HOFFMANN_BUSINESS_EVENT_LOCATION
                        : isFreeConsultation(selectedOption)
                        ? "Cremon 11, 20457 Hamburg"
                        : selectedOption.bookingMode === "consultation"
                          ? "Nach Vereinbarung"
                          : isStudioLikeBooking(selectedOption.bookingMode)
                            ? selectedStudioLocation
                            : "—"}
                    </strong>
                  </div>
                )}
                {selectedOption.bookingMode === "mobile" && (
                  <div className="summary-row">
                    <span>Einsatzort</span>
                    <strong>
                      {selectedFullAddress
                        ? selectedFullAddress
                        : "Bitte Adresse prüfen"}
                    </strong>
                  </div>
                )}
                {selectedOption.bookingMode === "mobile" && (
                  <div className="summary-row">
                    <span>Gebiet</span>
                    <strong>
                      {addressIsEligible
                        ? "Adresse geprüft"
                        : serviceAreaCheck.status === "checking"
                          ? "Prüfung läuft"
                          : serviceAreaCheck.status === "idle"
                            ? "Noch nicht geprüft"
                            : "Adresse nicht im Gebiet"}
                    </strong>
                  </div>
                )}
                {selectedOption.requiresVoucherQuestion && (
                  <div className="summary-row">
                    <span>Gutschein</span>
                    <strong>
                      {hasVoucher === null ? "Bitte auswählen" : hasVoucher ? "Ja" : "Nein"}
                    </strong>
                  </div>
                )}
                {selectedOption.badge && (
                  <div className="summary-row">
                    <span>Hinweis</span>
                    <strong>{selectedOption.badge}</strong>
                  </div>
                )}
                <div className="summary-row">
                  <span>Zahlung</span>
                  <strong>
                    {isFreeConsultation(selectedOption)
                      ? (
                        <span className="booking-free-visit-label">
                          Kostenfreier Erstbesuch
                        </span>
                      )
                      : voucherActive
                        ? (
                          <span className="booking-free-visit-label">
                            Kostenfreier Erstbesuch
                          </span>
                        )
                      : isFreeDueToReferralBalance
                        ? (
                          <span className="booking-free-visit-label">
                            Mit Empfehlungsguthaben
                          </span>
                        )
                      : selectedOption.paymentLocked
                        ? "Vor Ort"
                      : onlinePaymentOnly
                        ? "Online"
                      : effectivePaymentMethod === "stripe"
                        ? "Online"
                        : "Vor Ort"}
                  </strong>
                </div>
                {selectedOption.paidPackage && selectedOption.priceNetCents && (
                  <>
                    <div className="summary-row">
                      <span>Nettobetrag</span>
                      <strong>{formatPrice(selectedOption.priceNetCents)}</strong>
                    </div>
                    <div className="summary-row">
                      <span>MwSt.</span>
                      <strong>{formatPrice(packageVatCents)}</strong>
                    </div>
                  </>
                )}
                {currentPricePreview && (
                  <>
                    {currentPricePreview.serviceNetCents !== undefined &&
                      !isBusinessEventOption(selectedOption) && (
                      <div className="summary-row">
                        <span>
                          {isFixedGrossIndividualOption(selectedOption)
                            ? "Leistung"
                            : "Leistung netto"}
                        </span>
                        <strong>{formatPrice(currentPricePreview.serviceNetCents)}</strong>
                      </div>
                    )}
                    {currentPricePreview.travelNetCents !== undefined &&
                      !isBusinessEventOption(selectedOption) && (
                      <div className="summary-row">
                        <span>Anfahrt</span>
                        <strong>
                          {currentPricePreview.travelOriginalNetCents &&
                          currentPricePreview.travelOriginalNetCents !==
                            currentPricePreview.travelNetCents ? (
                            <>
                              {formatPrice(currentPricePreview.travelNetCents)}{" "}
                              <span
                                style={{
                                  textDecoration: "line-through",
                                  color: "var(--grey-mid)",
                                  fontWeight: 500,
                                }}
                              >
                                {formatPrice(currentPricePreview.travelOriginalNetCents)}
                              </span>
                            </>
                          ) : (
                            formatPrice(currentPricePreview.travelNetCents)
                          )}
                        </strong>
                      </div>
                    )}
                    {!isFixedGrossIndividualOption(selectedOption) &&
                      !isBusinessEventOption(selectedOption) && (
                      <>
                        <div className="summary-row">
                          <span>Netto gesamt</span>
                          <strong>{formatPrice(currentPricePreview.netCents)}</strong>
                        </div>
                        <div className="summary-row">
                          <span>MwSt. 19 %</span>
                          <strong>{formatPrice(currentPricePreview.vatCents)}</strong>
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="total">
                  <span>{currentPricePreview ? "Zu zahlen" : "Gesamt"}</span>
                  <strong>
                    {currentPricePreview ? (
                      <>
                        {formatPrice(
                          referralCreditPreviewCents > 0
                            ? payableAfterReferralCents
                            : currentPricePreview.grossCents
                        )}
                        {referralCreditPreviewCents > 0 && (
                          <small style={{ display: "block", fontWeight: 500 }}>
                            inkl. {formatPrice(referralCreditPreviewCents)} Guthaben
                          </small>
                        )}
                        {loadingPricePreview && (
                          <small style={{ display: "block", fontWeight: 500 }}>
                            Preis wird geprüft...
                          </small>
                        )}
                      </>
                    ) : isFreeConsultation(selectedOption) ? (
                      <>
                        <span
                          className="option-price-struck"
                          style={{
                            marginRight: "0.5rem",
                            fontSize: "0.9em",
                          }}
                        >
                          {selectedOption.price}{" "}
                          <small>{selectedOption.priceNote}</small>
                        </span>
                        <span className="booking-free-visit-label">
                          Kostenfreier Erstbesuch
                        </span>
                      </>
                    ) : voucherActive ? (
                      <>
                        <span
                          className="option-price-struck"
                          style={{
                            marginRight: "0.5rem",
                            fontSize: "0.9em",
                          }}
                        >
                          {formatPrice(paidBasePriceCents)}
                        </span>
                        <span className="booking-free-visit-label">
                          Kostenfreier Erstbesuch
                        </span>
                      </>
                    ) : durationChoiceMissing ? (
                      selectedOption.price
                    ) : (
                      <>
                        {formatPrice(
                          referralCreditPreviewCents > 0
                            ? payableAfterReferralCents
                            : effectivePriceCents
                        )}
                        {referralCreditPreviewCents > 0 && (
                          <small style={{ display: "block", fontWeight: 500 }}>
                            inkl. {formatPrice(referralCreditPreviewCents)} Guthaben
                          </small>
                        )}
                      </>
                    )}
                  </strong>
                </div>
              </div>

              {error && (
                <div className="auth-alert is-error" style={{ marginTop: "1rem" }}>
                  {error}
                  {errorAction && (
                    <div style={{ marginTop: "0.75rem" }}>
                      <Link to={errorAction.href} className="btn-ghost">
                        {errorAction.label}
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <div className="booking-consent-panel" aria-label="Rechtliche Bestätigungen">
                <label className="booking-checkbox">
                  <input
                    type="checkbox"
                    checked={bookingConsents.legalTerms}
                    onChange={(e) =>
                      setBookingConsents((current) => ({
                        ...current,
                        legalTerms: e.target.checked,
                      }))
                    }
                  />
                  <span>
                    Ich habe die{" "}
                    <Link to="/agbs" target="_blank" rel="noopener noreferrer">
                      AGB inklusive Widerrufsbelehrung
                    </Link>{" "}
                    und die{" "}
                    <Link to="/datenschutz" target="_blank" rel="noopener noreferrer">
                      Datenschutzerklärung
                    </Link>{" "}
                    gelesen und akzeptiere die AGB für diese Buchung.
                  </span>
                </label>
              </div>

              {!user && selectedOption.requiresAuth && (
                <div className="booking-auth-panel">
                  <p>
                    Zum Abschließen benötigen wir ein Kundenkonto. Deine Auswahl
                    bleibt erhalten, wenn du dich anmeldest oder kostenlos registrierst.
                  </p>
                  <div className="booking-auth-actions">
                    <Link
                      to={buildBuchenAuthHref("/einloggen", selectedOption.id)}
                      className="btn-primary"
                      onClick={persistDraft}
                    >
                      Anmelden
                    </Link>
                    <Link
                      to={buildBuchenAuthHref("/registrieren", selectedOption.id)}
                      className="btn-outline"
                      onClick={persistDraft}
                    >
                      Kostenlos registrieren
                    </Link>
                  </div>
                </div>
              )}

              <div className="booking-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setSelectedOption(null);
                    const clearedParams = new URLSearchParams(location.search);
                    clearedParams.delete("option");
                    const clearedQuery = clearedParams.toString();
                    navigate(clearedQuery ? `/buchen?${clearedQuery}` : "/buchen", {
                      replace: true,
                    });
                  }}
                >
                  Andere Option
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    submitting ||
                    businessEventBookingClosed ||
                    (!selectedSlot && !appointmentIsArranged) ||
                    (needsBookingPhone && !String(bookingPhone || "").trim()) ||
                    voucherChoiceMissing ||
                    (hasVoucher === true && !voucherTermsAccepted) ||
                    !bookingConsents.legalTerms ||
                    (requiresServiceAreaCheck && !addressIsEligible)
                  }
                  onClick={submitAuthedBooking}
                >
                  {submitting
                    ? "Buchung läuft…"
                    : !user && selectedOption.requiresAuth
                      ? "Buchung abschließen"
                    : isFreeConsultation(selectedOption)
                      ? "Kostenfreien Termin anfragen"
                    : voucherActive || isFreeDueToReferralBalance
                      ? "Kostenfrei buchen"
                      : onlinePaymentOnly && payableAfterReferralCents > 0
                        ? "Zur Zahlung"
                      : effectivePaymentMethod === "stripe" && isPaidBooking && payableAfterReferralCents > 0
                        ? "Zur Zahlung"
                        : "Jetzt verbindlich buchen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default BookingPage;
